import { useEffect, useMemo, useState } from "react";
import { http, VAT_RATES, getDeviceGrid, setDeviceGrid } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import OperatorsTab from "@/components/OperatorsTab";
import QRMenuPanel from "@/components/QRMenuPanel";
import PermissionsMatrixTab from "@/components/PermissionsMatrixTab";
import RoleDefaultsCard from "@/components/RoleDefaultsCard";
import DevicesTab from "@/components/DevicesTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import FTextOutbox from "@/components/FTextOutbox";
import BusinessLogoUploader from "@/components/BusinessLogoUploader";
import { SectionRow, ColorPaletteInput } from "@/components/RestaurantSectionsAdmin";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  isCompanionEnabled,
  setCompanionEnabled,
  subscribeCompanion,
  discoverWindowsPrinters,
} from "@/lib/companionQueue";
import { isKioskMode, setKioskMode } from "@/lib/kioskMode";
import { Save, Plus, Trash2, Send, Pencil, Printer, RefreshCw, Check, X as XIcon, ChevronUp, ChevronDown, ArrowRight, Download } from "lucide-react";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";

const SETTINGS_TAB_VALUES = ["categories", "comments", "quicknotes", "operators", "tables", "display", "qrmenu"];
const ADMIN_TAB_VALUES = ["driver", "business", "printers", "stations", "payments", "departments", "scale", "permissions", "devices"];
const ALL_TABS = [
  ["driver", "Driver"],
  ["business", "Στοιχεία Επιχείρησης"],
  ["printers", "Εκτυπωτές"],
  ["display", "Ρυθμίσεις Οθόνης"],
  ["tables", "Τραπέζια"],
  ["stations", "Σταθμοί"],
  ["payments", "Μέθοδοι Πληρωμής"],
  ["departments", "Τμήματα Ταμειακής"],
  ["categories", "Κατηγορίες"],
  ["comments", "Σχόλια"],
  ["quicknotes", "Έτοιμα Σχόλια"],
  ["scale", "Ζυγός"],
  ["operators", "Χειριστές"],
  ["permissions", "Δικαιώματα"],
  ["devices", "Συσκευές"],
  ["qrmenu", "QR Menu"],
];

export default function SettingsPage({ group = "settings" }) {
  const { user: currentUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [stations, setStations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productCountsByCategory, setProductCountsByCategory] = useState({});
  // Per-device grid overrides (localStorage). Reload from storage on mount
  // so the form reflects the current device's pinned values.
  const [deviceCommentsCols, setDeviceCommentsCols] = useState(() => getDeviceGrid("comments-cols") ?? "");
  const [deviceCommentsRows, setDeviceCommentsRows] = useState(() => getDeviceGrid("comments-rows") ?? "");
  const [tables, setTables] = useState([]);
  const [sections, setSections] = useState([]);
  const [newSection, setNewSection] = useState({ name: "", color: "#3B82F6" });
  const [printers, setPrinters] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentGroups, setCommentGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [subgroupPicker, setSubgroupPicker] = useState({ open: false });
  const [applyToCategoryPicker, setApplyToCategoryPicker] = useState({ open: false, autoOpen: false });
  const [removeFromCategoryPicker, setRemoveFromCategoryPicker] = useState({ open: false });
  const [newComment, setNewComment] = useState({
    name: "",
    radio_group: "",
    price_delta: 0,
    color: "#fbbf24",
    sort_order: 0,
  });
  const [tablesCountInput, setTablesCountInput] = useState(24);

  const [newStation, setNewStation] = useState({ name: "", result_filename: "", is_default: false });
  const [newPayment, setNewPayment] = useState({ payment_id: 2, name: "", pos_terminal_id: 0, pos_transaction_type: 0 });
  const [newDept, setNewDept] = useState({ number: 1, description: "", vat_number: 3, income_classification: 1, tax_exemption: 0, vagrancy_fee: 0 });
  const [newCat, setNewCat] = useState({ name: "", color: "#2563EB", sort_order: 0 });

  const load = async () => {
    const [s, st, p, d, c, t, pr, cg, cm, sec] = await Promise.all([
      http.get("/settings"),
      http.get("/stations"),
      http.get("/payments"),
      http.get("/departments"),
      http.get("/categories"),
      http.get("/tables"),
      http.get("/printers"),
      http.get("/comment-groups"),
      http.get("/comments"),
      http.get("/restaurant-sections"),
    ]);
    setSettings(s.data);
    setStations(st.data);
    setPayments(p.data);
    setDepartments(d.data);
    setCategories(c.data);
    setTables(t.data);
    setPrinters(pr.data || []);
    setCommentGroups(cg.data || []);
    setComments(cm.data || []);
    setSections(sec.data || []);
    setTablesCountInput(t.data.length || 24);
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      await http.put("/settings", settings);
      toast.success("Αποθηκεύτηκε");
    } catch (e) {
      toast.error("Σφάλμα");
    }
  };

  const addStation = async () => {
    if (!newStation.name) return;
    await http.post("/stations", newStation);
    setNewStation({ name: "", result_filename: "", is_default: false });
    toast.success("Σταθμός προστέθηκε");
    load();
  };
  const delStation = async (id) => {
    await http.delete(`/stations/${id}`);
    toast.success("Διαγράφηκε");
    load();
  };

  const addPayment = async () => {
    if (!newPayment.name) return;
    await http.post("/payments", newPayment);
    setNewPayment({ payment_id: 2, name: "", pos_terminal_id: 0, pos_transaction_type: 0 });
    toast.success("Πληρωμή προστέθηκε");
    load();
  };
  const delPayment = async (pid) => {
    await http.delete(`/payments/${pid}`);
    load();
  };
  const syncPayment = async (pid) => {
    const r = await http.post(`/payments/${pid}/sync-to-device`);
    if (r.data.ok) toast.success("Στάλθηκε στη συσκευή");
    else toast.error(r.data.result?.result_description || "Σφάλμα");
  };

  const addDept = async () => {
    if (!newDept.description) return;
    await http.post("/departments", newDept);
    setNewDept({ number: 1, description: "", vat_number: 3, income_classification: 1, tax_exemption: 0, vagrancy_fee: 0 });
    load();
  };
  const delDept = async (n) => {
    await http.delete(`/departments/${n}`);
    load();
  };
  const syncDept = async (n) => {
    const r = await http.post(`/departments/${n}/sync-to-device`);
    if (r.data.ok) toast.success("Στάλθηκε στη συσκευή");
    else toast.error(r.data.result?.result_description);
  };

  const addCat = async () => {
    if (!newCat.name) return;
    await http.post("/categories", newCat);
    setNewCat({ name: "", color: "#2563EB", sort_order: 0 });
    toast.success("Κατηγορία προστέθηκε");
    load();
  };
  const delCat = async (id) => {
    if (!(await confirmDialog("Διαγραφή κατηγορίας; Τα προϊόντα που την έχουν θα μείνουν χωρίς κατηγορία."))) return;
    await http.delete(`/categories/${id}`);
    toast.success("Διαγράφηκε");
    load();
  };
  const [editCat, setEditCat] = useState(null); // {id, name, color, sort_order, print_enabled, printer_ids[]}
  const saveCatEdit = async () => {
    if (!editCat?.name) {
      toast.error("Όνομα κατηγορίας απαραίτητο");
      return;
    }
    await http.put(`/categories/${editCat.id}`, {
      name: editCat.name,
      color: editCat.color,
      sort_order: Number(editCat.sort_order) || 0,
      print_enabled: !!editCat.print_enabled,
      printer_ids: editCat.printer_ids || [],
      // legacy: keep first as printer_id so older clients still see something
      printer_id: (editCat.printer_ids && editCat.printer_ids[0]) || null,
    });
    toast.success("Ενημερώθηκε");
    setEditCat(null);
    load();
  };

  // ---- Comment groups ----
  const addCommentGroup = async () => {
    if (!newGroupName.trim()) return;
    const r = await http.post("/comment-groups", { name: newGroupName, sort_order: commentGroups.length });
    setNewGroupName("");
    setActiveGroupId(r.data.id);
    toast.success("Ομάδα προστέθηκε");
    load();
  };
  const renameCommentGroup = async (g) => {
    const name = (await promptDialog("Νέο όνομα ομάδας:", g.name));
    if (!name || !name.trim()) return;
    await http.put(`/comment-groups/${g.id}`, { name: name.trim() });
    load();
  };
  const delCommentGroup = async (g) => {
    if (!(await confirmDialog(`Διαγραφή της ομάδας «${g.name}» και ΟΛΩΝ των σχολίων της;`))) return;
    await http.delete(`/comment-groups/${g.id}`);
    if (activeGroupId === g.id) setActiveGroupId(null);
    load();
  };
  const duplicateCommentGroup = async (g) => {
    const r = await http.post(`/comment-groups/${g.id}/duplicate`);
    setActiveGroupId(r.data.id);
    toast.success(`Αντιγράφηκε ως «${r.data.name}»`);
    load();
  };

  // ---- Comment lines (within a group) ----
  const addComment = async () => {
    if (!activeGroupId || !newComment.name.trim()) return;
    await http.post("/comments", {
      group_id: activeGroupId,
      name: newComment.name.trim(),
      radio_group: newComment.radio_group.trim() || null,
      price_delta: Number(newComment.price_delta) || 0,
      color: newComment.color || null,
      sort_order: Number(newComment.sort_order) || 0,
    });
    setNewComment({ name: "", radio_group: newComment.radio_group, price_delta: 0, color: newComment.color, sort_order: 0 });
    load();
  };
  const delComment = async (id) => {
    if (!(await confirmDialog("Διαγραφή σχολίου;"))) return;
    await http.delete(`/comments/${id}`);
    load();
  };
  const updateCommentField = async (c, patch) => {
    await http.put(`/comments/${c.id}`, patch);
    load();
  };
  const toggleSubgroupMultiple = async (radioGroup, multiple) => {
    if (!activeGroupId || !radioGroup) return;
    try {
      await http.post(`/comment-groups/${activeGroupId}/subgroup-multiple`, {
        radio_group: radioGroup,
        multiple,
      });
      load();
    } catch (e) {
      toast.error("Σφάλμα ενημέρωσης υποομάδας: " + (e?.response?.data?.detail || e.message));
    }
  };
  const importSubgroup = async (sourceRadioGroup) => {
    if (!activeGroupId) return;
    try {
      const r = await http.post(`/comment-groups/${activeGroupId}/import-subgroup`, {
        source_radio_group: sourceRadioGroup,
      });
      const imp = r.data?.imported || 0;
      const sk = r.data?.skipped || 0;
      if (imp === 0) toast.warning(`Δεν εισήχθησαν σχόλια — όλα τα ${sk} υπάρχουν ήδη.`);
      else toast.success(`Εισήχθησαν ${imp} σχόλια στην ομάδα` + (sk ? ` (παραλείφθηκαν ${sk} διπλά).` : "."));
      setSubgroupPicker({ open: false });
      load();
    } catch (e) {
      toast.error("Σφάλμα εισαγωγής: " + (e?.response?.data?.detail || e.message));
    }
  };

  const applyGroupToCategory = async (categoryId) => {
    if (!activeGroupId) return;
    try {
      const r = await http.post(`/comment-groups/${activeGroupId}/apply-to-category`, {
        category_id: categoryId,
        auto_open_comment: applyToCategoryPicker.autoOpen,
      });
      const up = r.data?.updated || 0;
      const al = r.data?.already_had || 0;
      const tot = r.data?.total_in_category || 0;
      if (tot === 0) toast.warning("Η κατηγορία δεν περιέχει προϊόντα.");
      else if (up === 0) toast.info(`Όλα τα ${al} προϊόντα είχαν ήδη την ομάδα — εφαρμόστηκαν μόνο τα flags.`);
      else toast.success(`Η ομάδα εφαρμόστηκε σε ${up} προϊόντα` + (al ? ` (${al} είχαν ήδη το tag).` : "."));
      setApplyToCategoryPicker({ open: false, autoOpen: false });
    } catch (e) {
      toast.error("Σφάλμα εφαρμογής: " + (e?.response?.data?.detail || e.message));
    }
  };

  // Load product counts per category when either picker opens.
  useEffect(() => {
    if (!applyToCategoryPicker.open && !removeFromCategoryPicker.open) return;
    (async () => {
      try {
        const r = await http.get("/products");
        const counts = {};
        for (const p of r.data || []) {
          counts[p.category_id] = (counts[p.category_id] || 0) + 1;
        }
        setProductCountsByCategory(counts);
      } catch {
        setProductCountsByCategory({});
      }
    })();
  }, [applyToCategoryPicker.open, removeFromCategoryPicker.open]);

  const removeGroupFromCategory = async (categoryId) => {
    if (!activeGroupId) return;
    try {
      const r = await http.post(`/comment-groups/${activeGroupId}/remove-from-category`, {
        category_id: categoryId,
        disable_allow_comment_if_empty: true,
      });
      const up = r.data?.updated || 0;
      const un = r.data?.untouched || 0;
      const tot = r.data?.total_in_category || 0;
      if (tot === 0) toast.warning("Η κατηγορία δεν περιέχει προϊόντα.");
      else if (up === 0) toast.info(`Κανένα προϊόν δεν είχε την ομάδα (${un} ελέγχθηκαν).`);
      else toast.success(`Αφαιρέθηκε από ${up} προϊόντα` + (un ? ` (${un} δεν την είχαν).` : "."));
      setRemoveFromCategoryPicker({ open: false });
    } catch (e) {
      toast.error("Σφάλμα αφαίρεσης: " + (e?.response?.data?.detail || e.message));
    }
  };
  const toggleComment = async (c) => {
    await http.put(`/comments/${c.id}`, { is_active: !c.is_active });
    load();
  };

  // ---- Reorder helpers (swap sort_order with neighbour) ----
  const moveItem = async (kind, item, direction) => {
    const url = kind === "group" ? "/comment-groups" : "/comments";
    const arr = kind === "group"
      ? [...commentGroups].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      : comments
          .filter((c) => c.group_id === item.group_id && (c.radio_group || null) === (item.radio_group || null))
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const i = arr.findIndex((x) => x.id === item.id);
    const j = i + direction;
    if (i < 0 || j < 0 || j >= arr.length) return;
    const a = arr[i], b = arr[j];
    // Ensure unique sort_orders even if both were 0 → renumber the whole list.
    arr.forEach((x, k) => (x.sort_order = k * 10));
    // swap
    const tmp = arr[i].sort_order;
    arr[i].sort_order = arr[j].sort_order;
    arr[j].sort_order = tmp;
    await Promise.all(arr.map((x) => http.put(`${url}/${x.id}`, { sort_order: x.sort_order })));
    load();
  };
  const moveCommentGroup = (g, dir) => moveItem("group", g, dir);
  const moveCommentLine = (c, dir) => moveItem("line", c, dir);

  // Move an entire radio_group bucket up/down within its parent CommentGroup.
  // Implementation: identify buckets ordered by their min sort_order, swap
  // sort_orders of all members between current bucket and neighbour bucket
  // by shifting their entire ranges.
  const moveSubGroup = async (groupId, radioKey, direction) => {
    const linesInGroup = comments
      .filter((c) => c.group_id === groupId)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    // Build buckets in current order
    const buckets = [];
    const map = new Map();
    linesInGroup.forEach((c) => {
      const k = c.radio_group || "__plain__";
      if (!map.has(k)) {
        map.set(k, { key: k, items: [] });
        buckets.push(map.get(k));
      }
      map.get(k).items.push(c);
    });
    const i = buckets.findIndex((b) => b.key === (radioKey || "__plain__"));
    const j = i + direction;
    if (i < 0 || j < 0 || j >= buckets.length) return;
    // Swap entire ranges by reordering buckets and re-numbering all members
    const newOrder = [...buckets];
    [newOrder[i], newOrder[j]] = [newOrder[j], newOrder[i]];
    let k = 0;
    const updates = [];
    newOrder.forEach((b) => {
      b.items.forEach((c) => {
        const newSO = k * 10;
        k += 1;
        if ((c.sort_order || 0) !== newSO) updates.push({ id: c.id, sort_order: newSO });
      });
    });
    await Promise.all(updates.map((u) => http.put(`/comments/${u.id}`, { sort_order: u.sort_order })));
    load();
  };

  const applyTableCount = async () => {
    const n = Math.max(1, Math.min(200, Number(tablesCountInput) || 1));
    await http.post("/tables/set-count", { count: n });
    toast.success(`Ορίστηκαν ${n} τραπέζια`);
    load();
  };

  const updateTable = async (id, patch) => {
    const t = tables.find((x) => x.id === id);
    if (!t) return;
    const body = { ...t, ...patch };
    delete body.id;
    await http.put(`/tables/${id}`, body);
    setTables((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  // ----- Restaurant Sections (Τμήματα Καταστήματος) -----
  const createSection = async () => {
    const nm = (newSection.name || "").trim();
    if (!nm) {
      toast.error("Δώσε όνομα τμήματος");
      return;
    }
    try {
      const r = await http.post("/restaurant-sections", {
        name: nm,
        color: newSection.color,
        display_order: sections.length,
      });
      setSections((prev) => [...prev, r.data]);
      setNewSection({ name: "", color: "#3B82F6" });
      toast.success(`Δημιουργήθηκε τμήμα «${nm}»`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα δημιουργίας");
    }
  };

  const updateSection = async (id, patch) => {
    try {
      const r = await http.put(`/restaurant-sections/${id}`, patch);
      setSections((prev) => prev.map((x) => (x.id === id ? r.data : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  const deleteSection = async (id) => {
    const sec = sections.find((s) => s.id === id);
    if (!sec) return;
    if (!(await confirmDialog(`Διαγραφή τμήματος «${sec.name}»;`))) return;
    try {
      await http.delete(`/restaurant-sections/${id}`);
      setSections((prev) => prev.filter((x) => x.id !== id));
      toast.success("Διαγράφηκε");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  if (!settings) return <div className="p-8 text-slate-500">Φόρτωση...</div>;

  return (
    <div className="h-full flex flex-col">
      {group === "settings" && (
        <div className="px-6 py-4 border-b border-slate-200 bg-grid">
          <h2 className="font-heading text-2xl font-bold">Ρυθμίσεις</h2>
          <div className="text-xs text-slate-500">Κατηγορίες, σχόλια, χειριστές, τραπέζια, οθόνη</div>
        </div>
      )}

      <div className={group === "settings" ? "flex-1 overflow-y-auto scroll-thin p-6" : ""}>
        <Tabs defaultValue={group === "settings" ? "categories" : "driver"} className="max-w-5xl">
          <TabsList data-testid="settings-tabs">
            {ALL_TABS.filter(([v]) =>
              (group === "settings" ? SETTINGS_TAB_VALUES : ADMIN_TAB_VALUES).includes(v) &&
              (v !== "qrmenu" || settings.modules_qrmenu === true),
            ).map(([v, label]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="driver">
            <Card className="p-6 space-y-4">
              <div>
                <Label>Φάκελος Εισόδου (Input folder)</Label>
                <Input
                  value={settings.input_folder}
                  onChange={(e) => setSettings({ ...settings, input_folder: e.target.value })}
                  className="font-mono"
                  data-testid="setting-input-folder"
                />
                <div className="text-xs text-slate-500 mt-1">
                  Π.χ. <code>C:\FText</code> – εδώ γράφονται τα .txt αρχεία.
                </div>
              </div>
              <div>
                <Label>Αρχείο Αποτελέσματος (Result file)</Label>
                <Input
                  value={settings.result_file}
                  onChange={(e) => setSettings({ ...settings, result_file: e.target.value })}
                  className="font-mono"
                  data-testid="setting-result-file"
                />
                <div className="text-xs text-slate-500 mt-1">
                  Π.χ. <code>C:\FText\result.res</code>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Χρονικό όριο αναμονής (sec)</Label>
                  <Input
                    type="number"
                    value={settings.poll_timeout_sec}
                    onChange={(e) =>
                      setSettings({ ...settings, poll_timeout_sec: parseInt(e.target.value) || 10 })
                    }
                    data-testid="setting-timeout"
                  />
                </div>
                <div>
                  <Label>Προεπιλεγμένο ΦΠΑ</Label>
                  <Select
                    value={String(settings.default_vat_rate)}
                    onValueChange={(v) => setSettings({ ...settings, default_vat_rate: Number(v) })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VAT_RATES.map((v) => (
                        <SelectItem key={v.code} value={String(v.code)}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={settings.simulator_mode}
                  onCheckedChange={(v) => setSettings({ ...settings, simulator_mode: v })}
                  data-testid="setting-simulator"
                />
                <div>
                  <div className="font-semibold text-sm">Λειτουργία Simulator</div>
                  <div className="text-xs text-slate-500">
                    Όταν είναι ενεργό, η εφαρμογή προσομοιώνει το αποτέλεσμα του driver χωρίς να
                    χρειάζεται πραγματική σύνδεση.
                  </div>
                </div>
              </div>
              <Button onClick={saveSettings} className="bg-blue-600 hover:bg-blue-700" data-testid="save-settings-btn">
                <Save size={14} className="mr-1.5" />
                Αποθήκευση
              </Button>
            </Card>

            <FTextOutbox />
          </TabsContent>

          <TabsContent value="business">
            <Card className="p-6 space-y-4">
              <BusinessLogoUploader
                value={settings.business_logo || ""}
                printEnabled={!!settings.business_logo_print_enabled}
                printProformaEnabled={!!settings.business_logo_print_proforma_enabled}
                onChange={(logoDataUrl) =>
                  setSettings({ ...settings, business_logo: logoDataUrl })
                }
                onPrintToggle={(enabled) =>
                  setSettings({ ...settings, business_logo_print_enabled: enabled })
                }
                onPrintProformaToggle={(enabled) =>
                  setSettings({ ...settings, business_logo_print_proforma_enabled: enabled })
                }
              />
              <div className="pt-4 border-t">
                <h3 className="font-heading text-lg font-semibold">Στοιχεία Επιχείρησης</h3>
                <p className="text-xs text-slate-500">
                  6 σειρές × έως 32 χαρακτήρες — εμφανίζονται στις αποδείξεις παραγγελίας (μη φορολογικές).
                  Πατήστε <strong>B</strong> δίπλα σε κάθε γραμμή για έντονη γραφή (bold).
                </p>
              </div>
              <div className="space-y-2 font-mono">
                {[0, 1, 2, 3, 4, 5].map((i) => {
                  const v = (settings.business_lines || ["", "", "", "", "", ""])[i] || "";
                  const boldArr = settings.business_lines_bold || [true, false, false, false, false, false];
                  const isBold = !!boldArr[i];
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 text-xs text-slate-500 font-bold">{i + 1}.</div>
                      <Input
                        value={v}
                        maxLength={32}
                        onChange={(e) => {
                          const arr = [...(settings.business_lines || ["", "", "", "", "", ""])];
                          while (arr.length < 6) arr.push("");
                          arr[i] = e.target.value.slice(0, 32);
                          setSettings({ ...settings, business_lines: arr });
                        }}
                        className={`font-mono ${isBold ? "font-bold" : ""}`}
                        data-testid={`business-line-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const arr = [...boldArr];
                          while (arr.length < 6) arr.push(false);
                          arr[i] = !arr[i];
                          setSettings({ ...settings, business_lines_bold: arr });
                        }}
                        data-testid={`business-line-${i}-bold`}
                        title={isBold ? "Αφαίρεση Bold" : "Έντονη γραφή"}
                        className={`w-9 h-9 rounded-md border-2 font-bold font-sans text-base transition-all ${
                          isBold
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-400 border-slate-300 hover:border-slate-500"
                        }`}
                      >
                        B
                      </button>
                      <div className="text-[10px] text-slate-400 w-12 text-right tabular">
                        {v.length}/32
                      </div>
                    </div>
                  );
                })}
              </div>

              <Button
                onClick={saveSettings}
                className="bg-blue-600 hover:bg-blue-700 h-11 font-bold"
                data-testid="save-business-btn"
              >
                <Save size={14} className="mr-1.5" />
                Αποθήκευση
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="printers">
            <PrintersPanel />
            <CompanionStatusPanel />
            <div className="mt-6">
              <ProductsPrintingAudit />
            </div>
          </TabsContent>

          <TabsContent value="display">
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="font-heading text-lg font-semibold">Ρυθμίσεις Οθόνης</h3>
                <p className="text-xs text-slate-500">
                  Καθόρισε πόσα κουτάκια θα εμφανίζονται ανά σειρά και πόσες γραμμές θα είναι
                  ορατές στην οθόνη πωλήσεων. Σχεδιασμένο για touch monitor — μεγαλύτερα νούμερα =
                  μικρότερα κουμπιά.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Products grid */}
                <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                  <div className="font-heading font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-600" />
                    Πλέγμα Προϊόντων
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Στήλες (cols)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="12"
                        value={settings.products_cols ?? 5}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            products_cols: Math.max(1, Math.min(12, Number(e.target.value) || 1)),
                          })
                        }
                        data-testid="setting-products-cols"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Σειρές ορατές (rows)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={settings.products_rows ?? 4}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            products_rows: Math.max(1, Math.min(10, Number(e.target.value) || 1)),
                          })
                        }
                        data-testid="setting-products-rows"
                      />
                    </div>
                  </div>
                  <GridPreview
                    cols={settings.products_cols ?? 5}
                    rows={settings.products_rows ?? 4}
                    color="#2563EB"
                  />
                  <div>
                    <Label className="text-xs">Όριο χαμηλού αποθέματος (πορτοκαλί badge)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={settings.stock_low_threshold ?? 5}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          stock_low_threshold: Math.max(0, Number(e.target.value) || 0),
                        })
                      }
                      data-testid="setting-stock-threshold"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Για είδη με «Έλεγχο αποθέματος»: πράσινο badge πάνω από το όριο,
                      πορτοκαλί στο όριο και κάτω, κόκκινο στο 0 ή αρνητικό.
                    </p>
                  </div>
                </div>

                {/* Categories grid */}
                <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                  <div className="font-heading font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-600" />
                    Πλέγμα Κατηγοριών
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Στήλες (cols)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="12"
                        value={settings.categories_cols ?? 6}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            categories_cols: Math.max(1, Math.min(12, Number(e.target.value) || 1)),
                          })
                        }
                        data-testid="setting-categories-cols"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Σειρές ορατές (rows)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="4"
                        value={settings.categories_rows ?? 1}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            categories_rows: Math.max(1, Math.min(4, Number(e.target.value) || 1)),
                          })
                        }
                        data-testid="setting-categories-rows"
                      />
                    </div>
                  </div>
                  <GridPreview
                    cols={settings.categories_cols ?? 6}
                    rows={settings.categories_rows ?? 1}
                    color="#10B981"
                  />
                </div>

                {/* Comments grid — independent sizing for the inline comment selector */}
                <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                  <div className="font-heading font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-600" />
                    Πλέγμα Σχολίων (Comment tiles)
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Στήλες (cols)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="12"
                        value={settings.comments_cols ?? 4}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            comments_cols: Math.max(1, Math.min(12, Number(e.target.value) || 1)),
                          })
                        }
                        data-testid="setting-comments-cols"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Σειρές ορατές (rows)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="8"
                        value={settings.comments_rows ?? 5}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            comments_rows: Math.max(1, Math.min(8, Number(e.target.value) || 1)),
                          })
                        }
                        data-testid="setting-comments-rows"
                      />
                    </div>
                  </div>
                  <GridPreview
                    cols={settings.comments_cols ?? 4}
                    rows={settings.comments_rows ?? 5}
                    color="#8B5CF6"
                  />
                  <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
                    <input
                      type="checkbox"
                      checked={!!settings.comments_subgroup_per_row}
                      onChange={(e) =>
                        setSettings({ ...settings, comments_subgroup_per_row: e.target.checked })
                      }
                      data-testid="setting-comments-subgroup-per-row"
                      className="w-4 h-4"
                    />
                    <span className="text-xs text-slate-700">
                      Κάθε υποομάδα ξεκινά σε νέα γραμμή (αλλιώς συνεχόμενη ροή)
                    </span>
                  </label>
                </div>
              </div>

              {/* Device-local override — values pinned only on this tablet */}
              <div className="border-2 border-amber-300 rounded-lg p-4 space-y-3 bg-amber-50/40">
                <div className="font-heading font-semibold flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-600" />
                  Τοπική Συσκευή (Local override) — μόνο για αυτό το tablet
                </div>
                <p className="text-xs text-slate-600">
                  Αν θες ένα tablet (π.χ. του μπαρ) να έχει διαφορετικό πλέγμα σχολίων από
                  τα υπόλοιπα, όρισε εδώ τοπικές τιμές. Αφήνοντάς τα κενά, χρησιμοποιείται η
                  global ρύθμιση. Οι τιμές αποθηκεύονται σε localStorage αυτής της συσκευής.
                </p>
                <div className="grid grid-cols-3 gap-3 items-end">
                  <div>
                    <Label className="text-xs">Σχόλια — Στήλες (cols)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      placeholder={`(global: ${settings.comments_cols ?? 4})`}
                      value={deviceCommentsCols}
                      onChange={(e) => setDeviceCommentsCols(e.target.value)}
                      onBlur={() => {
                        const v = deviceCommentsCols === "" ? null : Number(deviceCommentsCols);
                        setDeviceGrid("comments-cols", v);
                      }}
                      data-testid="device-comments-cols"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Σχόλια — Σειρές (rows)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="8"
                      placeholder={`(global: ${settings.comments_rows ?? 5})`}
                      value={deviceCommentsRows}
                      onChange={(e) => setDeviceCommentsRows(e.target.value)}
                      onBlur={() => {
                        const v = deviceCommentsRows === "" ? null : Number(deviceCommentsRows);
                        setDeviceGrid("comments-rows", v);
                      }}
                      data-testid="device-comments-rows"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="border-2 border-amber-400 bg-white hover:bg-amber-100 text-amber-900 font-semibold h-10"
                    onClick={() => {
                      setDeviceCommentsCols("");
                      setDeviceCommentsRows("");
                      setDeviceGrid("comments-cols", null);
                      setDeviceGrid("comments-rows", null);
                      toast.info("Τοπικά overrides εκκαθαρίστηκαν για αυτή τη συσκευή.");
                    }}
                    data-testid="device-grid-reset"
                  >
                    Καθαρισμός
                  </Button>
                </div>
              </div>

              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
                <strong>Παράδειγμα 4×3:</strong> 4 είδη ανά γραμμή και 3 ορατές γραμμές —
                συνολικά 12 κουμπιά πάνω από τη γραμμή κύλισης.
              </div>

              <Button
                onClick={saveSettings}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-display-btn"
              >
                <Save size={14} className="mr-1.5" />
                Αποθήκευση
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="tables">
            <Card className="p-6 space-y-5">
              <div>
                <h3 className="font-heading text-lg font-semibold">Τραπέζια Εστιατορίου</h3>
                <p className="text-xs text-slate-500">
                  Όρισε τον αριθμό των ενεργών τραπεζιών (1-200). Κάθε τραπέζι έχει όνομα,
                  αριθμό αντιστοίχισης στην ταμειακή (1-100 — REST_TABLE_*), τμήμα και χρώμα.
                </p>
              </div>

              <div className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-3">
                  <Label className="text-xs">Αριθμός ενεργών τραπεζιών</Label>
                  <Input
                    type="number"
                    min="1"
                    max="200"
                    value={tablesCountInput}
                    onChange={(e) => setTablesCountInput(e.target.value)}
                    data-testid="tables-count-input"
                  />
                </div>
                <div className="col-span-3">
                  <Button
                    onClick={applyTableCount}
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                    data-testid="apply-tables-count-btn"
                  >
                    Εφαρμογή ({tables.length} τώρα)
                  </Button>
                </div>
              </div>

              {/* Grid display settings for tables */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50">
                <div className="font-heading font-semibold flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full bg-emerald-600" />
                  Πλέγμα Εμφάνισης Τραπεζιών
                </div>
                <div className="grid grid-cols-4 gap-3 items-end">
                  <div>
                    <Label className="text-xs">Στήλες</Label>
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={settings.tables_cols ?? 6}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          tables_cols: Math.max(1, Math.min(12, Number(e.target.value) || 1)),
                        })
                      }
                      data-testid="setting-tables-cols"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Σειρές ορατές</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={settings.tables_rows ?? 4}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          tables_rows: Math.max(1, Math.min(10, Number(e.target.value) || 1)),
                        })
                      }
                      data-testid="setting-tables-rows"
                    />
                  </div>
                  <div className="col-span-2">
                    <Button
                      onClick={saveSettings}
                      className="bg-blue-600 hover:bg-blue-700 w-full"
                      data-testid="save-tables-grid-btn"
                    >
                      <Save size={14} className="mr-1.5" />
                      Αποθήκευση Πλέγματος
                    </Button>
                  </div>
                </div>
                <GridPreview
                  cols={settings.tables_cols ?? 6}
                  rows={settings.tables_rows ?? 4}
                  color="#10B981"
                />
              </div>

              {/* Restaurant Sections (Τμήματα Καταστήματος) — color-coded zones
                  used to filter the table grid and stamped onto kitchen tickets.
                  Future iterations will gate per-user access by section. */}
              <div className="border border-blue-200 rounded-lg p-4 space-y-3 bg-blue-50">
                <div className="font-heading font-semibold flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full bg-blue-600" />
                  Τμήματα Καταστήματος
                </div>
                <p className="text-xs text-slate-600">
                  Ομαδοποίησε τα τραπέζια σε ζώνες (π.χ. «ΠΑΡΑΛΙΑ», «ΒΕΡΑΝΤΑ», «ΠΑΣΟ»).
                  Το χρώμα χρησιμοποιείται ως φόντο στο κουμπί φίλτρου και ως πλαίσιο στο
                  τραπέζι. Δεν αντικαθιστά τα χρώματα κατάστασης (πράσινο/κίτρινο/πορτοκαλί/κόκκινο).
                </p>

                {/* Existing sections list */}
                {sections.length > 0 && (
                  <div className="space-y-2">
                    {sections.map((sec) => (
                      <SectionRow
                        key={sec.id}
                        section={sec}
                        onUpdate={(patch) => updateSection(sec.id, patch)}
                        onDelete={() => deleteSection(sec.id)}
                        tablesCount={tables.filter((t) => t.section_id === sec.id).length}
                      />
                    ))}
                  </div>
                )}

                {/* New section creator */}
                <div className="grid grid-cols-12 gap-2 items-end pt-2 border-t border-blue-200">
                  <div className="col-span-6">
                    <Label className="text-xs">Όνομα νέου τμήματος</Label>
                    <Input
                      value={newSection.name}
                      onChange={(e) => setNewSection({ ...newSection, name: e.target.value })}
                      placeholder="π.χ. ΠΑΡΑΛΙΑ"
                      className="h-8"
                      data-testid="new-section-name"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Χρώμα</Label>
                    <ColorPaletteInput
                      value={newSection.color}
                      onChange={(c) => setNewSection({ ...newSection, color: c })}
                      testId="new-section-color"
                    />
                  </div>
                  <div className="col-span-3">
                    <Button
                      onClick={createSection}
                      className="bg-blue-600 hover:bg-blue-700 w-full h-8"
                      data-testid="create-section-btn"
                    >
                      <Plus size={14} className="mr-1.5" /> Προσθήκη
                    </Button>
                  </div>
                </div>
              </div>

              {/* Tables list */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Όνομα</TableHead>
                      <TableHead className="w-24">Α/Α Ταμειακής</TableHead>
                      <TableHead className="w-40">Τμήμα</TableHead>
                      <TableHead className="w-44">Τμήμα Κατ/τος</TableHead>
                      <TableHead className="w-20">Χρώμα</TableHead>
                      <TableHead className="w-20">Προεπ.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tables.map((t) => (
                      <TableRow key={t.id} data-testid={`tbl-row-${t.position}`}>
                        <TableCell className="font-mono font-semibold">
                          {t.position}
                        </TableCell>
                        <TableCell>
                          <Input
                            value={t.name}
                            onChange={(e) =>
                              setTables((prev) =>
                                prev.map((x) =>
                                  x.id === t.id ? { ...x, name: e.target.value } : x
                                )
                              )
                            }
                            onBlur={(e) => updateTable(t.id, { name: e.target.value })}
                            className="h-8"
                            data-testid={`tbl-name-${t.position}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            value={t.device_number}
                            onChange={(e) => {
                              const n = Math.max(1, Math.min(100, Number(e.target.value) || 1));
                              setTables((prev) =>
                                prev.map((x) =>
                                  x.id === t.id ? { ...x, device_number: n } : x
                                )
                              );
                            }}
                            onBlur={(e) => {
                              const n = Math.max(1, Math.min(100, Number(e.target.value) || 1));
                              updateTable(t.id, { device_number: n });
                            }}
                            className="h-8 font-mono"
                            data-testid={`tbl-dev-${t.position}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={String(t.department)}
                            onValueChange={(v) => updateTable(t.id, { department: Number(v) })}
                          >
                            <SelectTrigger className="h-8" data-testid={`tbl-dept-${t.position}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {departments.map((d) => (
                                <SelectItem key={d.number} value={String(d.number)}>
                                  #{d.number} – {d.description}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={t.section_id || "__none__"}
                            onValueChange={(v) =>
                              updateTable(t.id, { section_id: v === "__none__" ? null : v })
                            }
                          >
                            <SelectTrigger className="h-8" data-testid={`tbl-section-${t.position}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Κανένα —</SelectItem>
                              {sections.map((s) => (
                                <SelectItem key={s.id} value={s.id}>
                                  <span className="inline-flex items-center gap-2">
                                    <span
                                      className="w-3 h-3 rounded-full inline-block"
                                      style={{ background: s.color }}
                                    />
                                    {s.name}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="color"
                            value={t.color}
                            onChange={(e) =>
                              setTables((prev) =>
                                prev.map((x) =>
                                  x.id === t.id ? { ...x, color: e.target.value } : x
                                )
                              )
                            }
                            onBlur={(e) => updateTable(t.id, { color: e.target.value })}
                            className="h-8 w-14 p-0.5"
                            data-testid={`tbl-color-${t.position}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div
                            className="h-8 w-full rounded text-white text-xs font-bold flex items-center justify-center relative"
                            style={{ background: t.color }}
                          >
                            <span>{t.name}</span>
                            <span className="absolute bottom-0.5 right-1 text-[9px] opacity-80 font-mono">
                              #{t.device_number}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {tables.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-slate-400 py-6">
                          Δεν υπάρχουν τραπέζια. Όρισε αριθμό και πάτα Εφαρμογή.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="stations">
            <Card className="p-6 space-y-4">
              <h3 className="font-heading text-lg font-semibold">Σταθμοί (Multi-station)</h3>
              <p className="text-xs text-slate-500">
                Κάθε σταθμός γράφει στο δικό του result file (RESULT_FILENAME) για παράλληλη
                λειτουργία.
              </p>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <Label className="text-xs">Όνομα</Label>
                  <Input
                    value={newStation.name}
                    onChange={(e) => setNewStation({ ...newStation, name: e.target.value })}
                    data-testid="new-station-name"
                  />
                </div>
                <div className="col-span-7">
                  <Label className="text-xs">Result filename (full path)</Label>
                  <Input
                    value={newStation.result_filename}
                    onChange={(e) =>
                      setNewStation({ ...newStation, result_filename: e.target.value })
                    }
                    placeholder="C:\FText\Station2.res"
                    className="font-mono"
                    data-testid="new-station-resfile"
                  />
                </div>
                <div className="col-span-2 flex items-end">
                  <Button
                    onClick={addStation}
                    disabled={!newStation.name}
                    data-testid="add-station-btn"
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                  >
                    <Plus size={14} className="mr-1.5" />
                    Προσθήκη
                  </Button>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Όνομα</TableHead>
                    <TableHead>Result file</TableHead>
                    <TableHead>Default</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stations.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="font-mono text-xs">{s.result_filename}</TableCell>
                      <TableCell>{s.is_default ? "✓" : ""}</TableCell>
                      <TableCell>
                        <Button size="icon" variant="ghost" onClick={() => delStation(s.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card className="p-6 space-y-4">
              <h3 className="font-heading text-lg font-semibold">Μέθοδοι Πληρωμής</h3>
              <p className="text-xs text-slate-500">
                ID 1 = Μετρητά (reserved). ID 2-30 για κάρτες/voucher/credit κ.λπ.
              </p>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-1">
                  <Label className="text-xs">ID</Label>
                  <Input
                    type="number"
                    min={2}
                    max={30}
                    value={newPayment.payment_id}
                    onChange={(e) =>
                      setNewPayment({ ...newPayment, payment_id: Number(e.target.value) })
                    }
                    data-testid="new-pay-id"
                  />
                </div>
                <div className="col-span-4">
                  <Label className="text-xs">Όνομα</Label>
                  <Input
                    value={newPayment.name}
                    onChange={(e) => setNewPayment({ ...newPayment, name: e.target.value })}
                    data-testid="new-pay-name"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">POS Terminal</Label>
                  <Input
                    type="number"
                    value={newPayment.pos_terminal_id}
                    onChange={(e) =>
                      setNewPayment({ ...newPayment, pos_terminal_id: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Transaction Type</Label>
                  <Input
                    type="number"
                    value={newPayment.pos_transaction_type}
                    onChange={(e) =>
                      setNewPayment({ ...newPayment, pos_transaction_type: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="col-span-3">
                  <Button onClick={addPayment} className="bg-blue-600 hover:bg-blue-700 w-full" data-testid="add-pay-btn">
                    <Plus size={14} className="mr-1.5" /> Προσθήκη
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Όνομα</TableHead>
                    <TableHead>POS</TableHead>
                    <TableHead>Trans Type</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono">#{p.payment_id}</TableCell>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>{p.pos_terminal_id}</TableCell>
                      <TableCell>{p.pos_transaction_type}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => syncPayment(p.payment_id)}>
                            <Send size={12} className="mr-1" /> Σύνδεση
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => delPayment(p.payment_id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="departments">
            <Card className="p-6 space-y-4">
              <div>
                <h3 className="font-heading text-lg font-semibold">Τμήματα Συσκευής (1-40)</h3>
                <div className="mt-2 px-3 py-2 bg-amber-50 border-2 border-amber-300 rounded-md text-amber-800 font-bold text-sm">
                  ⚠ ΠΡΟΣΟΧΗ ΠΡΕΠΕΙ ΝΑ ΕΙΝΑΙ ΟΜΟΙΑ ΜΕ ΤΗΝ ΤΑΜΕΙΑΚΗ
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-1">
                  <Label className="text-xs">#</Label>
                  <Select
                    value={String(newDept.number)}
                    onValueChange={(v) => setNewDept({ ...newDept, number: Number(v) })}
                  >
                    <SelectTrigger data-testid="new-dept-num"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {Array.from({ length: 40 }, (_, i) => i + 1).map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <Label className="text-xs">Περιγραφή</Label>
                  <Input
                    value={newDept.description}
                    onChange={(e) =>
                      setNewDept({ ...newDept, description: e.target.value })
                    }
                    data-testid="new-dept-desc"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">VAT</Label>
                  <Select
                    value={String(newDept.vat_number)}
                    onValueChange={(v) => setNewDept({ ...newDept, vat_number: Number(v) })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VAT_RATES.map((v) => (
                        <SelectItem key={v.code} value={String(v.code)}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Income Class.</Label>
                  <Input
                    type="number"
                    value={newDept.income_classification}
                    onChange={(e) =>
                      setNewDept({ ...newDept, income_classification: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="col-span-3">
                  <Button onClick={addDept} className="bg-blue-600 hover:bg-blue-700 w-full" data-testid="add-dept-btn">
                    <Plus size={14} className="mr-1.5" /> Προσθήκη
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Περιγραφή</TableHead>
                    <TableHead>VAT</TableHead>
                    <TableHead>IncClass</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {departments.map((d) => (
                    <TableRow key={d.number}>
                      <TableCell className="font-mono">{d.number}</TableCell>
                      <TableCell className="font-medium">{d.description}</TableCell>
                      <TableCell>{VAT_RATES.find((v) => v.code === d.vat_number)?.label || d.vat_number}</TableCell>
                      <TableCell>{d.income_classification}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => syncDept(d.number)}>
                            <Send size={12} className="mr-1" /> Σύνδεση
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => delDept(d.number)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="categories">
            <Card className="p-6 space-y-4">
              <h3 className="font-heading text-lg font-semibold">Κατηγορίες Προϊόντων</h3>
              <p className="text-xs text-slate-500">
                Οι κατηγορίες εμφανίζονται ως πλήκτρα φιλτραρίσματος πάνω δεξιά στην οθόνη
                πωλήσεων.
              </p>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  <Label className="text-xs">Όνομα</Label>
                  <Input
                    value={newCat.name}
                    onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                    data-testid="new-cat-name"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Χρώμα</Label>
                  <Input
                    type="color"
                    value={newCat.color}
                    onChange={(e) => setNewCat({ ...newCat, color: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Σειρά</Label>
                  <Input
                    type="number"
                    value={newCat.sort_order}
                    onChange={(e) => setNewCat({ ...newCat, sort_order: Number(e.target.value) || 0 })}
                  />
                </div>
                <div className="col-span-3">
                  <Button
                    onClick={addCat}
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                    disabled={!newCat.name}
                    data-testid="add-cat-btn"
                  >
                    <Plus size={14} className="mr-1.5" /> Προσθήκη
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Όνομα</TableHead>
                    <TableHead>Σειρά</TableHead>
                    <TableHead>Εκτυπωτής</TableHead>
                    <TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((c) => {
                    const ids = (c.printer_ids && c.printer_ids.length > 0)
                      ? c.printer_ids
                      : (c.printer_id ? [c.printer_id] : []);
                    const cps = ids.map((id) => printers.find((p) => p.id === id)).filter(Boolean);
                    return (
                    <TableRow key={c.id} data-testid={`cat-row-${c.id}`}>
                      <TableCell>
                        <span
                          className="w-5 h-5 rounded-full inline-block"
                          style={{ background: c.color || "#2563EB" }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{c.sort_order}</TableCell>
                      <TableCell>
                        {c.print_enabled === false ? (
                          <span className="text-xs text-slate-400 italic">— off —</span>
                        ) : cps.length === 0 ? (
                          <span className="text-xs text-slate-400 italic">— default —</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {cps.map((p) => (
                              <span key={p.id} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200">
                                <Printer size={10} />
                                <span className="font-bold">{p.name}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() =>
                              setEditCat({
                                id: c.id,
                                name: c.name,
                                color: c.color || "#2563EB",
                                sort_order: c.sort_order || 0,
                                print_enabled: c.print_enabled !== false,
                                printer_ids: ids,
                              })
                            }
                            data-testid={`edit-cat-${c.id}`}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => delCat(c.id)}
                            className="text-red-600"
                            data-testid={`del-cat-${c.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {categories.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-400 py-6">
                        Καμία κατηγορία ακόμη.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="comments">
            <Card className="p-6 space-y-4">
              <div>
                <h3 className="font-heading text-lg font-semibold">Σχόλια Παραγγελιών</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Οργανώνονται σε <b>ομάδες</b> (π.χ. «Έξτρα καφέ», «Παρασκευή σαλάτας»). Μέσα σε
                  κάθε ομάδα, σχόλια με ίδια τιμή «Σύνολο επιλογών» γίνονται radio-set (διαλέγεις
                  ένα). Όσα έχουν κενό «Σύνολο» μπαίνουν μόνα τους. Με «Επιπλέον €» η επιλογή
                  αυξάνει την τιμή του είδους.
                </p>
              </div>

              <div className="grid grid-cols-[280px_1fr] gap-4">
                {/* Left: groups list */}
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="Νέα ομάδα…"
                      onKeyDown={(e) => { if (e.key === "Enter") addCommentGroup(); }}
                      data-testid="new-comment-group-name"
                      className="h-9 text-sm"
                    />
                    <Button
                      onClick={addCommentGroup}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 h-9 shrink-0"
                      disabled={!newGroupName.trim()}
                      data-testid="add-comment-group-btn"
                    >
                      <Plus size={14} />
                    </Button>
                  </div>
                  <div className="space-y-1" data-testid="comment-groups-list">
                    {commentGroups.length === 0 && (
                      <div className="text-xs text-slate-400 italic py-4 text-center">
                        Δεν υπάρχουν ομάδες ακόμη.
                      </div>
                    )}
                    {commentGroups.map((g, gi) => {
                      const count = comments.filter((c) => c.group_id === g.id).length;
                      const active = activeGroupId === g.id;
                      return (
                        <div
                          key={g.id}
                          className={`flex items-center gap-1 group rounded-md border-2 ${active ? "border-blue-500 bg-white" : "border-transparent hover:bg-white"}`}
                          data-testid={`comment-group-row-${g.id}`}
                        >
                          <div className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => moveCommentGroup(g, -1)}
                              disabled={gi === 0}
                              className="p-0.5 text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              data-testid={`mvup-comment-group-${g.id}`}
                              title="Πάνω"
                            >
                              <ChevronUp size={11} />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveCommentGroup(g, +1)}
                              disabled={gi === commentGroups.length - 1}
                              className="p-0.5 text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              data-testid={`mvdn-comment-group-${g.id}`}
                              title="Κάτω"
                            >
                              <ChevronDown size={11} />
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => setActiveGroupId(g.id)}
                            className="flex-1 px-2 py-1.5 text-left text-sm font-medium truncate"
                          >
                            {g.name}
                            <span className="ml-1 text-[10px] text-slate-400">({count})</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => duplicateCommentGroup(g)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-slate-500 hover:text-emerald-600"
                            data-testid={`dup-comment-group-${g.id}`}
                            title="Αντιγραφή"
                          >
                            <Plus size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => renameCommentGroup(g)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-slate-500 hover:text-blue-600"
                            data-testid={`rename-comment-group-${g.id}`}
                            title="Μετονομασία"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => delCommentGroup(g)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-slate-500 hover:text-red-600"
                            data-testid={`del-comment-group-${g.id}`}
                            title="Διαγραφή"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right: comments of the active group */}
                <div className="border border-slate-200 rounded-lg p-3 bg-white">
                  {!activeGroupId ? (
                    <div className="text-center text-slate-400 py-10">
                      Επίλεξε μια ομάδα από αριστερά για επεξεργασία.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-12 gap-2 items-end pb-2 border-b border-slate-100">
                        <div className="col-span-4">
                          <Label className="text-[11px]">Όνομα σχολίου</Label>
                          <Input
                            value={newComment.name}
                            onChange={(e) => setNewComment({ ...newComment, name: e.target.value })}
                            placeholder="π.χ. Σκέτος"
                            list="all-comment-names"
                            className="h-9 text-sm"
                            onKeyDown={(e) => { if (e.key === "Enter") addComment(); }}
                            data-testid="new-comment-name"
                          />
                          <datalist id="all-comment-names">
                            {Array.from(new Set(comments.map((c) => c.name))).sort().map((n) => (
                              <option key={n} value={n} />
                            ))}
                          </datalist>
                        </div>
                        <div className="col-span-3">
                          <Label className="text-[11px]">Σύνολο επιλογών (radio)</Label>
                          <select
                            value={newComment.radio_group}
                            onChange={async (e) => {
                              const v = e.target.value;
                              if (v === "__new__") {
                                const lbl = (await promptDialog("Όνομα νέου συνόλου (π.χ. γλυκύτητα):"));
                                if (lbl && lbl.trim()) {
                                  setNewComment({ ...newComment, radio_group: lbl.trim() });
                                }
                              } else {
                                setNewComment({ ...newComment, radio_group: v });
                              }
                            }}
                            className="h-9 px-2 border-2 border-slate-200 rounded-md bg-white text-sm w-full"
                            data-testid="new-comment-radio"
                          >
                            <option value="">— απλό (καμία ομάδα) —</option>
                            {(() => {
                              const localRg = Array.from(
                                new Set(
                                  comments
                                    .filter((c) => c.group_id === activeGroupId && c.radio_group)
                                    .map((c) => c.radio_group),
                                ),
                              );
                              const otherRg = Array.from(
                                new Set(
                                  comments
                                    .filter((c) => c.group_id !== activeGroupId && c.radio_group)
                                    .map((c) => c.radio_group),
                                ),
                              ).filter((rg) => !localRg.includes(rg));
                              return (
                                <>
                                  {localRg.map((rg) => (
                                    <option key={`l-${rg}`} value={rg}>{rg}</option>
                                  ))}
                                  {otherRg.length > 0 && (
                                    <option disabled>──── από άλλες ομάδες ────</option>
                                  )}
                                  {otherRg.map((rg) => (
                                    <option key={`o-${rg}`} value={rg}>↳ {rg}</option>
                                  ))}
                                </>
                              );
                            })()}
                            <option value="__new__">+ Νέο σύνολο…</option>
                          </select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[11px]">+ € extra</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newComment.price_delta}
                            onChange={(e) => setNewComment({ ...newComment, price_delta: e.target.value })}
                            className="h-9 text-sm font-mono"
                            data-testid="new-comment-price"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[11px]">Χρώμα</Label>
                          <Input
                            type="color"
                            value={newComment.color || "#fbbf24"}
                            onChange={(e) => setNewComment({ ...newComment, color: e.target.value })}
                            className="h-9 p-0.5 cursor-pointer"
                            data-testid="new-comment-color"
                          />
                        </div>
                        <div className="col-span-1">
                          <Button
                            onClick={addComment}
                            className="bg-blue-600 hover:bg-blue-700 h-9 w-full"
                            disabled={!newComment.name.trim()}
                            data-testid="add-comment-btn"
                          >
                            <Plus size={14} />
                          </Button>
                        </div>
                      </div>

                      {/* Quick action: import an existing radio_group (subgroup)
                          from any other comment group — clones every comment
                          belonging to that subgroup into the current group. */}
                      <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
                        <div className="text-xs text-slate-500 flex-1 min-w-[200px]">
                          Έτοιμη υποομάδα ή εφαρμογή σε προϊόντα κατηγορίας:
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSubgroupPicker({ open: true })}
                            className="h-8 border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-900 font-semibold"
                            data-testid="import-subgroup-btn"
                          >
                            <Plus size={14} className="mr-1" /> Εισαγωγή υποομάδας
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setApplyToCategoryPicker({ open: true, autoOpen: false })}
                            className="h-8 border-2 border-emerald-400 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-semibold"
                            data-testid="apply-to-category-btn"
                          >
                            <ArrowRight size={14} className="mr-1" /> Εφαρμογή σε κατηγορία
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setRemoveFromCategoryPicker({ open: true })}
                            className="h-8 border-2 border-red-400 bg-red-50 hover:bg-red-100 text-red-900 font-semibold"
                            data-testid="remove-from-category-btn"
                          >
                            <XIcon size={14} className="mr-1" /> Αφαίρεση από κατηγορία
                          </Button>
                        </div>
                      </div>

                      {/* Comments list — grouped by radio_group for visual clarity */}
                      <div className="space-y-3" data-testid="comments-list">
                        {(() => {
                          const groupComments = comments
                            .filter((c) => c.group_id === activeGroupId)
                            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                          if (groupComments.length === 0) {
                            return (
                              <div className="text-center text-slate-400 py-6 text-sm">
                                Δεν υπάρχουν σχόλια σε αυτή την ομάδα.
                              </div>
                            );
                          }
                          // Build buckets: keep first-seen order
                          const buckets = [];
                          const map = new Map();
                          groupComments.forEach((c) => {
                            const key = c.radio_group || "__plain__";
                            if (!map.has(key)) {
                              map.set(key, { key, label: c.radio_group, items: [] });
                              buckets.push(map.get(key));
                            }
                            map.get(key).items.push(c);
                          });
                          return buckets.map((b, bi) => {
                            const isRadio = b.key !== "__plain__";
                            return (
                              <div
                                key={b.key}
                                className={`rounded-lg border-2 p-2 ${isRadio ? "border-purple-300 bg-purple-50/30" : "border-slate-200 bg-white"}`}
                                data-testid={`comment-bucket-${b.key}`}
                              >
                                <div className="flex items-center justify-between mb-1.5 px-1">
                                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                                    {isRadio ? (
                                      <>
                                        <div className="flex flex-col">
                                          <button
                                            type="button"
                                            onClick={() => moveSubGroup(activeGroupId, b.label, -1)}
                                            disabled={bi === 0}
                                            className="text-purple-500 hover:text-purple-700 disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                                            data-testid={`mvup-subgroup-${b.label}`}
                                            title="Πάνω"
                                          >
                                            <ChevronUp size={12} />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => moveSubGroup(activeGroupId, b.label, +1)}
                                            disabled={bi === buckets.length - 1}
                                            className="text-purple-500 hover:text-purple-700 disabled:opacity-30 disabled:cursor-not-allowed leading-none"
                                            data-testid={`mvdn-subgroup-${b.label}`}
                                            title="Κάτω"
                                          >
                                            <ChevronDown size={12} />
                                          </button>
                                        </div>
                                        <span className="text-purple-700">
                                          {b.items.some((x) => x.radio_multiple)
                                            ? `☑ Υποομάδα «${b.label}» — πολλαπλή επιλογή`
                                            : `◉ Υποομάδα «${b.label}» — διάλεξε ΕΝΑ`}
                                        </span>
                                      </>
                                    ) : (
                                      <span>☑ Απλά σχόλια — διάλεξε όσα θες</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {isRadio && (
                                      <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                        <span className="text-[10px] text-slate-600 normal-case">
                                          Πολλαπλή
                                        </span>
                                        <Switch
                                          checked={b.items.some((x) => x.radio_multiple)}
                                          onCheckedChange={(v) => toggleSubgroupMultiple(b.label, v)}
                                          data-testid={`multi-subgroup-${b.label}`}
                                          title="Επιτρέπεται επιλογή πολλών σχολίων από αυτή την υποομάδα"
                                        />
                                      </label>
                                    )}
                                    <span className="text-[10px] text-slate-400">{b.items.length}</span>
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  {b.items.map((c, ci) => (
                            <div
                              key={c.id}
                              className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 rounded-md border border-slate-100 bg-white hover:bg-slate-50"
                              data-testid={`comment-row-${c.id}`}
                            >
                              <div className="col-span-1 flex flex-col items-center">
                                <button
                                  type="button"
                                  onClick={() => moveCommentLine(c, -1)}
                                  disabled={ci === 0}
                                  className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                  data-testid={`mvup-comment-${c.id}`}
                                >
                                  <ChevronUp size={14} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveCommentLine(c, +1)}
                                  disabled={ci === b.items.length - 1}
                                  className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                  data-testid={`mvdn-comment-${c.id}`}
                                >
                                  <ChevronDown size={14} />
                                </button>
                              </div>
                              <input
                                type="color"
                                className="col-span-1 h-7 w-full p-0 cursor-pointer rounded border border-slate-200"
                                value={c.color || "#94a3b8"}
                                onChange={(e) => updateCommentField(c, { color: e.target.value })}
                              />
                              <Input
                                className="col-span-2 h-8 text-sm"
                                value={c.name}
                                list="all-comment-names"
                                onChange={(e) => updateCommentField(c, { name: e.target.value })}
                              />
                              <select
                                className="col-span-2 h-8 px-2 border-2 border-slate-200 rounded-md bg-white text-xs"
                                value={c.radio_group || ""}
                                onChange={async (e) => {
                                  const v = e.target.value;
                                  if (v === "__new__") {
                                    const lbl = (await promptDialog("Όνομα νέας υποομάδας:"));
                                    if (lbl && lbl.trim()) updateCommentField(c, { radio_group: lbl.trim() });
                                  } else {
                                    updateCommentField(c, { radio_group: v || null });
                                  }
                                }}
                              >
                                <option value="">— απλό —</option>
                                {/* Reusable subgroups: list radio_groups from THIS group first
                                    (for quick same-group reuse), then radio_groups defined elsewhere
                                    in other comment groups (separated visually). This lets the user
                                    pin a comment to a subgroup name that already exists across the
                                    catalogue so naming stays consistent. */}
                                {(() => {
                                  const localRg = Array.from(
                                    new Set(
                                      comments
                                        .filter((x) => x.group_id === activeGroupId && x.radio_group)
                                        .map((x) => x.radio_group),
                                    ),
                                  );
                                  const otherRg = Array.from(
                                    new Set(
                                      comments
                                        .filter((x) => x.group_id !== activeGroupId && x.radio_group)
                                        .map((x) => x.radio_group),
                                    ),
                                  ).filter((rg) => !localRg.includes(rg));
                                  return (
                                    <>
                                      {localRg.map((rg) => (
                                        <option key={`l-${rg}`} value={rg}>{rg}</option>
                                      ))}
                                      {otherRg.length > 0 && (
                                        <option disabled>──── άλλες ομάδες ────</option>
                                      )}
                                      {otherRg.map((rg) => (
                                        <option key={`o-${rg}`} value={rg}>↳ {rg}</option>
                                      ))}
                                    </>
                                  );
                                })()}
                                <option value="__new__">+ Νέα υποομάδα…</option>
                              </select>
                              <div className="col-span-2 relative">
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="h-8 text-base font-mono font-bold pr-7 text-emerald-700 bg-emerald-50 border-emerald-300 focus:border-emerald-500"
                                  value={c.price_delta}
                                  onChange={(e) => updateCommentField(c, { price_delta: Number(e.target.value) || 0 })}
                                  data-testid={`price-comment-${c.id}`}
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-700 font-bold text-sm pointer-events-none">
                                  €
                                </span>
                              </div>
                              <div className="col-span-1 flex items-center justify-center">
                                <Switch
                                  checked={!!c.splittable}
                                  onCheckedChange={(v) => updateCommentField(c, { splittable: v })}
                                  data-testid={`split-comment-${c.id}`}
                                  title="Σπαστό (−/+)"
                                />
                              </div>
                              <div className="col-span-1 flex items-center justify-center">
                                <Switch
                                  checked={!!c.is_deduction}
                                  onCheckedChange={(v) => updateCommentField(c, { is_deduction: v })}
                                  data-testid={`deduction-comment-${c.id}`}
                                  title="Αφαίρεση τιμής αντί προσθήκης"
                                />
                              </div>
                              <div className="col-span-2 flex items-center justify-end gap-1">
                                <Switch
                                  checked={c.is_active}
                                  onCheckedChange={() => toggleComment(c)}
                                  data-testid={`toggle-comment-${c.id}`}
                                />
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => delComment(c.id)}
                                  className="text-red-600 h-7 w-7"
                                  data-testid={`del-comment-${c.id}`}
                                >
                                  <Trash2 size={13} />
                                </Button>
                              </div>
                            </div>
                                  ))}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="quicknotes">
            <QuickNotesTab />
          </TabsContent>

          <TabsContent value="scale">
            <Card className="p-6 space-y-5 max-w-2xl">
              <div>
                <h3 className="font-heading text-lg font-bold flex items-center gap-2">Ζυγός — Σύνθετα EAN-13 barcodes</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Τα ζυγιστικά barcodes ακολουθούν το πρότυπο GS1 EAN-13. Τα δύο πρώτα ψηφία είναι το πρόθεμα
                  (διακρίνει ΒΑΡΟΣ ή ΑΞΙΑ), τα ψηφία 3-7 ο κωδικός είδους (5-ψήφιος), τα 8-12 η τιμή/ποσότητα και
                  το 13ο ψηφίο ελέγχου. Οι συντελεστές πολλαπλασιάζουν τα 5 ψηφία τιμής/βάρους.
                </p>
              </div>

              <div className="flex items-center gap-3 p-3 border-2 border-teal-200 bg-teal-50/40 rounded-lg">
                <Switch
                  checked={!!settings.scale_enabled}
                  onCheckedChange={(v) => setSettings({ ...settings, scale_enabled: v })}
                  data-testid="scale-enabled"
                />
                <div className="flex-1">
                  <div className="font-bold text-sm text-teal-800">Ενεργοποίηση αναγνώρισης ζυγού</div>
                  <div className="text-[11px] text-slate-600">
                    Όταν είναι ενεργοποιημένο, το POS αναγνωρίζει αυτόματα τα prefixes στο πεδίο αναζήτησης (barcode scanner).
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="grid gap-1">
                  <Label>Πρόθεμα barcode για ΒΑΡΟΣ</Label>
                  <Input
                    value={settings.scale_weight_prefix || ""}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        scale_weight_prefix: e.target.value.replace(/\D/g, "").slice(0, 2),
                      })
                    }
                    placeholder="21"
                    maxLength={2}
                    className="font-mono text-lg"
                    data-testid="scale-weight-prefix"
                  />
                  <div className="text-[11px] text-slate-500">2 ψηφία — προτεινόμενη τιμή: 21</div>
                </div>
                <div className="grid gap-1">
                  <Label>Συντελεστής ΒΑΡΟΥΣ</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={settings.scale_weight_coefficient ?? 0.001}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        scale_weight_coefficient: parseFloat(e.target.value) || 0.001,
                      })
                    }
                    className="font-mono text-lg"
                    data-testid="scale-weight-coefficient"
                  />
                  <div className="text-[11px] text-slate-500">
                    π.χ. 0.001 → grams σε kg (default κατά GS1).
                  </div>
                </div>

                <div className="grid gap-1">
                  <Label>Πρόθεμα barcode για ΤΙΜΗ</Label>
                  <Input
                    value={settings.scale_price_prefix || ""}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        scale_price_prefix: e.target.value.replace(/\D/g, "").slice(0, 2),
                      })
                    }
                    placeholder="29"
                    maxLength={2}
                    className="font-mono text-lg"
                    data-testid="scale-price-prefix"
                  />
                  <div className="text-[11px] text-slate-500">2 ψηφία — προτεινόμενη τιμή: 29</div>
                </div>
                <div className="grid gap-1">
                  <Label>Συντελεστής ΑΞΙΑΣ</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={settings.scale_price_coefficient ?? 0.01}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        scale_price_coefficient: parseFloat(e.target.value) || 0.01,
                      })
                    }
                    className="font-mono text-lg"
                    data-testid="scale-price-coefficient"
                  />
                  <div className="text-[11px] text-slate-500">π.χ. 0.01 → cents σε €.</div>
                </div>
              </div>

              {/* Example */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs space-y-1">
                <div className="font-bold text-slate-700">Παράδειγμα:</div>
                <div className="font-mono">
                  <span className="text-teal-700 font-bold">{settings.scale_weight_prefix || "21"}</span>
                  <span className="text-slate-700">00015</span>
                  <span className="text-emerald-700">02750</span>
                  <span className="text-slate-400">0</span>
                  {" "} → ΠΑΤΑΤΕΣ (κωδ. 00015), βάρος 2,750 kg
                </div>
                <div className="font-mono">
                  <span className="text-amber-700 font-bold">{settings.scale_price_prefix || "29"}</span>
                  <span className="text-slate-700">00114</span>
                  <span className="text-emerald-700">00555</span>
                  <span className="text-slate-400">0</span>
                  {" "} → ΚΟΤΟΠΟΥΛΟ (κωδ. 00114), αξία 5,55 €
                </div>
              </div>

              {settings.scale_weight_prefix &&
                settings.scale_price_prefix &&
                settings.scale_weight_prefix === settings.scale_price_prefix && (
                <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs font-bold">
                  Τα 2 prefixes πρέπει να είναι διαφορετικά.
                </div>
              )}

              <Button
                onClick={saveSettings}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={
                  !settings.scale_weight_prefix ||
                  !settings.scale_price_prefix ||
                  settings.scale_weight_prefix.length !== 2 ||
                  settings.scale_price_prefix.length !== 2 ||
                  settings.scale_weight_prefix === settings.scale_price_prefix
                }
                data-testid="save-scale-btn"
              >
                Αποθήκευση
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="operators">
            <OperatorsTab currentUser={currentUser} />
          </TabsContent>

          <TabsContent value="permissions">
            <div className="space-y-6">
              <PermissionsMatrixTab currentUser={currentUser} />
              <RoleDefaultsCard />
            </div>
          </TabsContent>

          <TabsContent value="devices">
            <DevicesTab />
          </TabsContent>

          <TabsContent value="qrmenu">
            <QRMenuPanel settings={settings} onSettingsChange={setSettings} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit category dialog */}
      {editCat && (
        <Dialog open={!!editCat} onOpenChange={(o) => !o && setEditCat(null)}>
          <DialogContent className="max-w-md" hideClose data-testid="edit-cat-dialog">
            <DialogHeader>
              <DialogTitle>Επεξεργασία Κατηγορίας</DialogTitle>
            </DialogHeader>
            <div className="grid gap-3">
              <div className="grid gap-1">
                <Label>Όνομα</Label>
                <Input
                  value={editCat.name}
                  onChange={(e) => setEditCat({ ...editCat, name: e.target.value })}
                  data-testid="edit-cat-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1">
                  <Label>Χρώμα</Label>
                  <Input
                    type="color"
                    value={editCat.color}
                    onChange={(e) => setEditCat({ ...editCat, color: e.target.value })}
                    data-testid="edit-cat-color"
                  />
                </div>
                <div className="grid gap-1">
                  <Label>Σειρά</Label>
                  <Input
                    type="number"
                    value={editCat.sort_order}
                    onChange={(e) =>
                      setEditCat({ ...editCat, sort_order: Number(e.target.value) || 0 })
                    }
                    data-testid="edit-cat-sort"
                  />
                </div>
              </div>
              <div className="grid gap-2 border-t border-slate-200 pt-3">
                <label className="flex items-center gap-3 p-3 border-2 border-emerald-200 bg-emerald-50/40 rounded-lg cursor-pointer hover:bg-emerald-50">
                  <Switch
                    checked={!!editCat.print_enabled}
                    onCheckedChange={(v) => setEditCat({ ...editCat, print_enabled: v })}
                    data-testid="edit-cat-print-enabled"
                  />
                  <div className="flex-1">
                    <div className="font-bold text-sm flex items-center gap-1.5 text-emerald-800">
                      <Printer size={14} /> ΕΚΤΥΠΩΣΗ
                    </div>
                    <div className="text-[11px] text-slate-600">
                      Τα προϊόντα της κατηγορίας στέλνονται σε εκτυπωτή παραγγελίας
                    </div>
                  </div>
                </label>
                {editCat.print_enabled && (
                  <div className="grid grid-cols-2 gap-1.5 p-3 border-2 border-slate-200 rounded-lg" data-testid="edit-cat-printer-list">
                    {printers.filter((p) => p.active && !p.is_reports).length === 0 && (
                      <div className="col-span-2 text-xs text-slate-500 italic">
                        Δεν υπάρχουν δηλωμένοι εκτυπωτές. Πήγαινε στο tab «Εκτυπωτές».
                      </div>
                    )}
                    {printers.filter((p) => p.active && !p.is_reports).map((p) => {
                      const checked = (editCat.printer_ids || []).includes(p.id);
                      return (
                        <label
                          key={p.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded border-2 text-xs cursor-pointer ${checked ? "border-emerald-500 bg-white" : "border-slate-200 bg-white/60 hover:bg-white"}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...(editCat.printer_ids || []), p.id]
                                : (editCat.printer_ids || []).filter((x) => x !== p.id);
                              setEditCat({ ...editCat, printer_ids: next });
                            }}
                            data-testid={`edit-cat-printer-${p.id}`}
                          />
                          <span className="font-bold">{p.name}</span>
                          <span className="text-slate-400">({p.printer_type === "a4" ? "Α4" : "80mm"})</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="flex flex-row justify-end gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => setEditCat(null)}
                className="h-11 font-bold border-2"
                data-testid="edit-cat-cancel"
              >
                Ακύρωση
              </Button>
              <Button
                onClick={saveCatEdit}
                className="bg-blue-600 hover:bg-blue-700 h-11 font-bold"
                data-testid="edit-cat-save"
              >
                Καταχώρηση
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Subgroup picker — clone an existing radio_group's comments into the active group */}
      <Dialog open={subgroupPicker.open} onOpenChange={(o) => !o && setSubgroupPicker({ open: false })}>
        <DialogContent className="max-w-2xl" data-testid="subgroup-picker-dialog">
          <DialogHeader>
            <DialogTitle>Εισαγωγή υπάρχουσας υποομάδας</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="text-xs text-slate-500 px-1">
              Πάτησε μια υποομάδα για να εισαχθούν όλα τα σχόλιά της στην τρέχουσα ομάδα. Υπάρχοντα ονόματα παραλείπονται.
            </div>
            {(() => {
              // Build per-group lists: { groupName, radioGroups: [{rg, count}] }
              const groupById = Object.fromEntries(commentGroups.map((g) => [g.id, g.name]));
              const rows = [];
              for (const g of commentGroups) {
                const cs = comments.filter((c) => c.group_id === g.id && c.radio_group);
                const rgMap = new Map();
                for (const c of cs) {
                  rgMap.set(c.radio_group, (rgMap.get(c.radio_group) || 0) + 1);
                }
                if (rgMap.size === 0) continue;
                rows.push({ groupName: g.name, groupId: g.id, items: [...rgMap.entries()].map(([rg, count]) => ({ rg, count })) });
              }
              if (rows.length === 0) {
                return <div className="text-center text-slate-400 py-6">Δεν υπάρχουν υποομάδες πουθενά.</div>;
              }
              return rows.map((row) => (
                <div key={row.groupId} className="border border-slate-200 rounded-lg">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-700">
                    {row.groupName}
                    {row.groupId === activeGroupId && (
                      <span className="ml-2 text-amber-700 font-normal lowercase">(τρέχουσα ομάδα)</span>
                    )}
                  </div>
                  <div className="p-2 flex flex-wrap gap-2">
                    {row.items.map((it) => (
                      <button
                        key={it.rg}
                        type="button"
                        onClick={() => importSubgroup(it.rg)}
                        data-testid={`import-subgroup-pill-${it.rg}`}
                        className="inline-flex items-center gap-2 px-3 h-9 bg-white border-2 border-amber-300 rounded-lg hover:bg-amber-50 hover:border-amber-500 active:scale-95 transition-all"
                      >
                        <span className="font-semibold text-sm text-slate-800">{it.rg}</span>
                        <span className="text-[10px] font-mono bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">
                          {it.count} σχόλια
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubgroupPicker({ open: false })} data-testid="subgroup-picker-cancel">
              Ακύρωση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply-to-category picker — attach the active comment group to every product
          of a chosen product category in a single click. */}
      <Dialog
        open={applyToCategoryPicker.open}
        onOpenChange={(o) => !o && setApplyToCategoryPicker({ open: false, autoOpen: false })}
      >
        <DialogContent className="max-w-2xl" data-testid="apply-to-category-dialog">
          <DialogHeader>
            <DialogTitle>Εφαρμογή ομάδας σε προϊόντα κατηγορίας</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="text-xs text-slate-500 px-1">
              Επίλεξε κατηγορία προϊόντων. Η τρέχουσα ομάδα σχολίων θα προστεθεί σε
              όλα τα προϊόντα της κατηγορίας και θα ενεργοποιηθεί το flag «επιτρέπει σχόλιο».
            </div>
            <label className="flex items-center gap-2 px-1 py-2 bg-slate-50 rounded-md border border-slate-200 cursor-pointer">
              <input
                type="checkbox"
                checked={applyToCategoryPicker.autoOpen}
                onChange={(e) =>
                  setApplyToCategoryPicker((s) => ({ ...s, autoOpen: e.target.checked }))
                }
                data-testid="apply-to-category-auto-open"
                className="w-4 h-4"
              />
              <span className="text-sm text-slate-700">
                Αυτόματο άνοιγμα διαλόγου σχολίων όταν προστίθεται προϊόν (auto-open)
              </span>
            </label>
            {(() => {
              if (!categories || categories.length === 0) {
                return <div className="text-center text-slate-400 py-6">Δεν υπάρχουν κατηγορίες προϊόντων.</div>;
              }
              return (
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((c) => {
                    const count = productCountsByCategory[c.id] || 0;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => applyGroupToCategory(c.id)}
                        disabled={count === 0}
                        data-testid={`apply-to-category-pill-${c.name}`}
                        className="flex items-center justify-between px-3 h-12 rounded-lg border-2 border-emerald-300 bg-white hover:bg-emerald-50 hover:border-emerald-500 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <span className="font-semibold text-sm text-slate-800 truncate">{c.name}</span>
                        <span className="text-[10px] font-mono bg-emerald-100 text-emerald-800 rounded px-2 py-0.5 shrink-0 ml-2">
                          {count} προϊόντα
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApplyToCategoryPicker({ open: false, autoOpen: false })}
              data-testid="apply-to-category-cancel"
            >
              Ακύρωση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove-from-category picker — detach the active comment group from every
          product of a chosen product category in a single click. */}
      <Dialog
        open={removeFromCategoryPicker.open}
        onOpenChange={(o) => !o && setRemoveFromCategoryPicker({ open: false })}
      >
        <DialogContent className="max-w-2xl" data-testid="remove-from-category-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Αφαίρεση ομάδας από προϊόντα κατηγορίας</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="text-xs text-slate-600 px-1 bg-red-50 border border-red-200 rounded p-2">
              ⚠️ Επίλεξε κατηγορία προϊόντων. Η τρέχουσα ομάδα σχολίων θα αφαιρεθεί από
              όλα τα προϊόντα της κατηγορίας. Όσα προϊόντα μείνουν χωρίς καμία ομάδα
              σχολίων, το flag «επιτρέπει σχόλιο» θα απενεργοποιηθεί.
            </div>
            {(() => {
              if (!categories || categories.length === 0) {
                return <div className="text-center text-slate-400 py-6">Δεν υπάρχουν κατηγορίες προϊόντων.</div>;
              }
              return (
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((c) => {
                    const count = productCountsByCategory[c.id] || 0;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => removeGroupFromCategory(c.id)}
                        disabled={count === 0}
                        data-testid={`remove-from-category-pill-${c.name}`}
                        className="flex items-center justify-between px-3 h-12 rounded-lg border-2 border-red-300 bg-white hover:bg-red-50 hover:border-red-500 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <span className="font-semibold text-sm text-slate-800 truncate">{c.name}</span>
                        <span className="text-[10px] font-mono bg-red-100 text-red-800 rounded px-2 py-0.5 shrink-0 ml-2">
                          {count} προϊόντα
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRemoveFromCategoryPicker({ open: false })}
              data-testid="remove-from-category-cancel"
            >
              Ακύρωση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GridPreview({ cols, rows, color }) {
  const cells = Array.from({ length: cols * rows }, (_, i) => i);
  return (
    <div className="mt-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        Προεπισκόπηση {cols}×{rows}
      </div>
      <div
        className="grid gap-1 bg-slate-50 border border-slate-200 rounded p-2"
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridAutoRows: "28px",
        }}
      >
        {cells.map((i) => (
          <div
            key={i}
            className="rounded-sm border"
            style={{ background: color + "22", borderColor: color }}
          />
        ))}
      </div>
    </div>
  );
}


// ============================================================
// Multi-Printer Management Panel (Virtual ↔ Windows mapping)
// ============================================================
const COMPANION_URL = "http://127.0.0.1:9100";

/** Audit + bulk-fix panel for product printer routing.
 *
 * Surfaces the «Espresso τυπώνεται σε ΟΛΑ τα τμήματα» class of bugs:
 * products with `printer_override=true` plus 2+ printer_ids emit one
 * ticket per printer when an order is submitted. The panel reads
 * `/api/admin/products/printing-audit` and offers two one-click fixes:
 *
 *   • «Καθαρισμός override» → falls back to category routing (recommended)
 *   • «Όλα στον επιλεγμένο εκτυπωτή» → keeps override but collapses to
 *     a single printer (handy when categories lack routing).
 */
function ProductsPrintingAudit() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [targetPid, setTargetPid] = useState("");

  const load = async () => {
    try {
      const [a, pr] = await Promise.all([
        http.get("/admin/products/printing-audit"),
        http.get("/printers"),
      ]);
      setData(a.data);
      setPrinters(pr.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία ανάκτησης");
    }
  };

  useEffect(() => { load(); }, []);

  const fixClearAll = async () => {
    if (!(await confirmDialog("Καθαρισμός override σε όλα τα προϊόντα με πολλαπλούς εκτυπωτές;\n\nΘα κληρονομούν routing από την κατηγορία τους."))) return;
    setBusy(true);
    try {
      const r = await http.post("/admin/products/printing-fix", {
        only_multi: true,
        action: "clear_override",
      });
      toast.success(`✅ Διορθώθηκαν ${r.data.updated} προϊόντα`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  const fixSetSingle = async () => {
    if (!targetPid) { toast.error("Επίλεξε εκτυπωτή πρώτα"); return; }
    if (!(await confirmDialog(`Όλα τα προβληματικά προϊόντα να τυπώνονται μόνο στον επιλεγμένο εκτυπωτή;`))) return;
    setBusy(true);
    try {
      const r = await http.post("/admin/products/printing-fix", {
        only_multi: true,
        action: "set_single",
        target_printer_id: targetPid,
      });
      toast.success(`✅ Διορθώθηκαν ${r.data.updated} προϊόντα`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  const fixOne = async (productId, action, tpid = null) => {
    setBusy(true);
    try {
      const r = await http.post("/admin/products/printing-fix", {
        product_ids: [productId],
        only_multi: false,
        action,
        target_printer_id: tpid,
      });
      if (r.data.updated > 0) toast.success("✅ Διορθώθηκε");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const { rows = [], summary = {} } = data;
  const multi = rows.filter((r) => r.kind === "multi");
  const empty = rows.filter((r) => r.kind === "empty");
  const none = rows.filter((r) => r.kind === "none");
  const problematic = [...multi, ...empty];

  return (
    <Card className="p-6 space-y-4" data-testid="products-printing-audit">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <span className="text-base">🔍</span> Έλεγχος Δρομολόγησης Προϊόντων
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Εντοπίζει προϊόντα που τυπώνονται σε ΛΑΘΟΣ τμήματα λόγω ξεχασμένου override.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="audit-refresh">
          Ανανέωση
        </Button>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className={`px-2.5 py-1 rounded-full font-bold ${summary.multi > 0 ? "bg-red-100 text-red-700 border border-red-300" : "bg-slate-100 text-slate-500"}`}>
          🔴 Πολλαπλοί εκτυπωτές: {summary.multi || 0}
        </span>
        <span className={`px-2.5 py-1 rounded-full font-bold ${summary.empty > 0 ? "bg-amber-100 text-amber-700 border border-amber-300" : "bg-slate-100 text-slate-500"}`}>
          🟡 Override χωρίς προορισμό: {summary.empty || 0}
        </span>
        <span className={`px-2.5 py-1 rounded-full ${summary.none > 0 ? "bg-slate-200 text-slate-700" : "bg-slate-100 text-slate-500"}`}>
          ⚪ Χωρίς routing: {summary.none || 0}
        </span>
        <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">
          ✅ OK: {summary.ok || 0}
        </span>
      </div>

      {/* Bulk fix actions */}
      {problematic.length > 0 && (
        <div className="border border-red-200 bg-red-50/40 rounded-lg p-3 space-y-2.5">
          <div className="text-sm font-semibold text-red-800">
            ⚠ Βρέθηκαν {problematic.length} προϊόντα με προβληματική δρομολόγηση
          </div>
          <div className="text-xs text-slate-600">
            Επιλέξτε διορθωτική ενέργεια για όλα μαζί:
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="default"
              onClick={fixClearAll}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="audit-fix-clear-all"
            >
              🧹 Καθαρισμός override (κληρονόμηση από κατηγορία)
            </Button>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-slate-600">ή στείλε όλα στον:</span>
              <select
                value={targetPid}
                onChange={(e) => setTargetPid(e.target.value)}
                className="h-8 px-2 border border-slate-300 rounded text-xs"
                data-testid="audit-target-printer"
              >
                <option value="">— επιλογή —</option>
                {printers.filter((p) => p.active !== false).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} → {p.windows_printer_name}
                  </option>
                ))}
              </select>
              <Button size="sm" variant="outline" onClick={fixSetSingle} disabled={busy || !targetPid} data-testid="audit-fix-set-single">
                Εφαρμογή
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Per-product table */}
      {problematic.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 sticky top-0">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold">Προϊόν</th>
                <th className="px-3 py-2 font-semibold">Κατηγορία</th>
                <th className="px-3 py-2 font-semibold">Τυπώνεται σε</th>
                <th className="px-3 py-2 font-semibold text-right">Ενέργεια</th>
              </tr>
            </thead>
            <tbody>
              {problematic.map((r) => (
                <tr key={r.id} className={`border-t ${r.kind === "multi" ? "bg-red-50/30" : "bg-amber-50/30"}`} data-testid={`audit-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.description}</td>
                  <td className="px-3 py-2 text-slate-600">{r.category_name || "—"}</td>
                  <td className="px-3 py-2">
                    {r.effective_printer_names.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {r.effective_printer_names.map((n, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded bg-red-200 text-red-900 font-mono text-[10px]">{n}</span>
                        ))}
                      </div>
                    ) : <span className="text-amber-700 italic">(κενό)</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fixOne(r.id, "clear_override")}
                      disabled={busy}
                      className="h-7 text-[11px]"
                      data-testid={`audit-fix-one-${r.id}`}
                    >
                      Καθαρισμός
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {problematic.length === 0 && (
        <div className="text-center text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-4">
          ✨ Όλα τα προϊόντα έχουν σωστή δρομολόγηση εκτυπωτή.
        </div>
      )}

      {none.length > 0 && (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer hover:text-slate-700">
            {none.length} προϊόντα δεν έχουν καθόλου routing (δεν τυπώνονται)
          </summary>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
            {none.slice(0, 30).map((r) => (
              <div key={r.id} className="px-2 py-1 bg-slate-50 rounded">{r.description}</div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
}



function PrintersPanel() {
  const [printers, setPrinters] = useState([]);
  const [winPrinters, setWinPrinters] = useState([]); // discovered Windows printers
  const [winRoutes, setWinRoutes] = useState([]); // v3 print engine route ids
  const [winStatus, setWinStatus] = useState("unknown"); // "ok" | "offline" | "unknown"
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // PrinterProfile or "new"

  const load = async () => {
    try {
      const r = await http.get("/printers");
      setPrinters(r.data || []);
    } catch {
      /* noop */
    }
  };
  const discoverWindows = async () => {
    try {
      const [pRes, hRes] = await Promise.all([
        fetch(`${COMPANION_URL}/printers`),
        fetch(`${COMPANION_URL}/health`).catch(() => null),
      ]);
      if (!pRes.ok) throw new Error("offline");
      const j = await pRes.json();
      setWinPrinters(j.printers || []);
      // v3 print engine routes (kitchen/bar/cashier/tameio …) — empty for v2.
      let routes = [];
      if (hRes && hRes.ok) {
        try {
          const h = await hRes.json();
          if (h && typeof h.routes === "object") routes = Object.keys(h.routes);
          else if (h && typeof h.printer_profiles === "object") routes = Object.keys(h.printer_profiles);
        } catch { /* health body not JSON */ }
      }
      setWinRoutes(routes);
      setWinStatus("ok");
      toast.success(
        `Companion ΟΚ — ${j.printers.length} εκτυπωτές${routes.length ? ` · ${routes.length} v3 routes` : ""}.`,
      );
    } catch {
      setWinPrinters([]);
      setWinRoutes([]);
      setWinStatus("offline");
      toast.error("Companion offline — λειτουργία manual εισαγωγής.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    load();
    // Also try discovery silently on mount (no toast).
    Promise.all([
      fetch(`${COMPANION_URL}/printers`).then((r) => (r.ok ? r.json() : Promise.reject())),
      fetch(`${COMPANION_URL}/health`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ])
      .then(([j, h]) => {
        if (cancelled) return;
        setWinPrinters(j.printers || []);
        let routes = [];
        if (h && typeof h.routes === "object") routes = Object.keys(h.routes);
        else if (h && typeof h.printer_profiles === "object") routes = Object.keys(h.printer_profiles);
        setWinRoutes(routes);
        setWinStatus("ok");
      })
      .catch(() => {
        if (!cancelled) setWinStatus("offline");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSave = async (data, isNew) => {
    setBusy(true);
    try {
      if (isNew) {
        await http.post("/printers", data);
        toast.success("Δημιουργήθηκε");
      } else {
        await http.put(`/printers/${editing.id}`, data);
        toast.success("Αποθηκεύτηκε");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (p) => {
    if (!(await confirmDialog(`Διαγραφή «${p.name}»;`))) return;
    try {
      await http.delete(`/printers/${p.id}`);
      toast.success("Διαγράφηκε");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    }
  };

  const onTestPrint = async (p) => {
    try {
      const text =
        `Ponte Pos\n` +
        `Δοκιμαστική εκτύπωση\n` +
        `Virtual: ${p.name}\n` +
        `Target: ${p.windows_printer_name || "(default)"}\n` +
        `Τύπος: ${p.printer_type}\n` +
        `--------------------------------------\nOK!\n\n\n`;
      const id = (p.windows_printer_name || "").trim();
      // /print-ticket → v3 formatter applies bold title + cut. v2 agents
      // that only understand `printer` field will ignore route/profile.
      const r = await fetch(`${COMPANION_URL}/print-ticket`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ route: id, profile: id, printer: id, text }),
      });
      const j = await r.json();
      if (j.ok) toast.success(`Εκτυπώθηκε στον ${j.printer || j.route || id}`);
      else toast.error(j.error || "Σφάλμα");
    } catch {
      toast.error("Companion offline — αδυναμία εκτύπωσης");
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Printer size={18} /> Εκτυπωτές
          </h3>
          <p className="text-xs text-slate-500">
            Διαχείριση <strong>Virtual</strong> εκτυπωτών — εικονικά ονόματα μέσα στο πρόγραμμα
            που μπορούν να δείχνουν στον ίδιο ή σε διαφορετικούς πραγματικούς εκτυπωτές Windows.
            Πολλαπλοί virtual εκτυπωτές → ένας πραγματικός είναι αποδεκτό.
          </p>
        </div>
        <Button
          onClick={() => setEditing("new")}
          className="bg-emerald-600 hover:bg-emerald-700 h-10"
          data-testid="new-printer-btn"
        >
          <Plus size={14} className="mr-1.5" /> Νέος Εκτυπωτής
        </Button>
      </div>

      {/* Companion status banner */}
      <div
        className={`rounded-md border-2 p-3 text-sm flex items-center gap-3 ${
          winStatus === "ok"
            ? "bg-emerald-50 border-emerald-300 text-emerald-900"
            : winStatus === "offline"
            ? "bg-amber-50 border-amber-300 text-amber-900"
            : "bg-slate-50 border-slate-300 text-slate-700"
        }`}
        data-testid="companion-status"
      >
        <div
          className={`w-2.5 h-2.5 rounded-full ${
            winStatus === "ok" ? "bg-emerald-500" : winStatus === "offline" ? "bg-amber-500" : "bg-slate-400"
          }`}
        />
        <div className="flex-1">
          {winStatus === "ok" && (
            <>
              <strong>Companion ΟΚ</strong> — {winPrinters.length} Windows εκτυπωτές διαθέσιμοι για επιλογή.
            </>
          )}
          {winStatus === "offline" && (
            <>
              <strong>Companion offline</strong> — μπορείς να εισάγεις το όνομα του εκτυπωτή χειροκίνητα.
              Η εκτύπωση θα γίνεται μέσω browser dialog μέχρι να εγκατασταθεί το service.
            </>
          )}
          {winStatus === "unknown" && <>Ανίχνευση Companion service…</>}
        </div>
        <Button variant="outline" size="sm" onClick={discoverWindows} data-testid="discover-windows-btn">
          <RefreshCw size={13} className="mr-1" /> Ανίχνευση
        </Button>
      </div>

      {/* Companion auto-print toggle — per-device opt-in */}
      <CompanionAutoPrintToggle />

      {/* Kiosk Mode (Chrome --kiosk-printing) — silent HTML prints */}
      <KioskModeToggle />

      {/* Print Jobs Monitor — last 50 jobs */}
      <PrintJobsMonitor printers={printers} />

      {/* Printer list */}
      {printers.length === 0 ? (
        <div className="text-center text-slate-400 py-10 text-sm border-2 border-dashed border-slate-200 rounded-md">
          Δεν υπάρχουν εκτυπωτές. Πάτησε «Νέος Εκτυπωτής» για να προσθέσεις.
        </div>
      ) : (
        <div className="space-y-2">
          {printers.map((p) => (
            <PrinterRow
              key={p.id}
              p={p}
              onEdit={() => setEditing(p)}
              onDelete={() => onDelete(p)}
              onTest={() => onTestPrint(p)}
            />
          ))}
        </div>
      )}

      {editing && (
        <PrinterFormDialog
          mode={editing === "new" ? "new" : "edit"}
          initial={editing === "new" ? null : editing}
          winPrinters={winPrinters}
          winRoutes={winRoutes}
          winStatus={winStatus}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={(data) => onSave(data, editing === "new")}
        />
      )}

      {/* Installation guide */}
      <div className="bg-slate-50 border-2 border-slate-200 rounded-md p-3 text-xs text-slate-700 space-y-1.5 mt-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="font-bold text-sm text-slate-900">
            Εγκατάσταση Companion Print Service (μία φορά ανά POS PC)
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/companion/agent-latest.zip`}
            download
            data-testid="companion-download-link"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm active:scale-[0.97] transition-all no-underline"
          >
            <Download size={14} />
            Λήψη Local Agent (.zip)
          </a>
        </div>
        <div className="font-mono text-[11px] bg-white border border-slate-200 rounded p-2">
          1. Κατέβασε το ZIP από πάνω και αποσυμπίεσέ το στο <code>C:\FText\</code>
          <br />
          2. Εκτέλεση <code>PontePOS-Companion.exe</code> — θα ξεκινήσει αυτόματα με τα Windows
          <br />
          3. Πατήστε «Ανίχνευση» για επαλήθευση σύνδεσης
        </div>
      </div>
    </Card>
  );
}

function PrinterRow({ p, onEdit, onDelete, onTest }) {
  const typeBadge = p.printer_type === "a4"
    ? { label: "Α4", color: "bg-blue-100 text-blue-800 border-blue-300" }
    : { label: "80mm", color: "bg-orange-100 text-orange-800 border-orange-300" };
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-md border-2 ${
        p.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"
      }`}
      data-testid={`printer-row-${p.id}`}
    >
      <div className={`w-10 h-10 rounded-md grid place-items-center ${
        p.active ? "bg-slate-900 text-white" : "bg-slate-300 text-slate-500"
      }`}>
        <Printer size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm truncate">{p.name}</span>
          <span className={`text-[10px] uppercase px-2 py-0.5 rounded border font-bold ${typeBadge.color}`}>
            {typeBadge.label}
          </span>
          {p.is_default && (
            <span className="text-[10px] uppercase px-2 py-0.5 rounded border font-bold bg-emerald-100 text-emerald-800 border-emerald-300">
              Default
            </span>
          )}
          {p.is_reports && (
            <span className="text-[10px] uppercase px-2 py-0.5 rounded border font-bold bg-purple-100 text-purple-800 border-purple-300">
              Αναφορές
            </span>
          )}
          {!p.active && (
            <span className="text-[10px] uppercase px-2 py-0.5 rounded border font-bold bg-slate-200 text-slate-600 border-slate-300">
              Ανενεργός
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 font-mono mt-0.5 truncate">
          Windows: {p.windows_printer_name || <em>(default)</em>}
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onTest} className="h-9" data-testid={`printer-test-${p.id}`}>
        <Send size={13} className="mr-1" /> Δοκιμή
      </Button>
      <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`printer-edit-${p.id}`}>
        <Pencil size={14} />
      </Button>
      <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-600" data-testid={`printer-del-${p.id}`}>
        <Trash2 size={14} />
      </Button>
    </div>
  );
}

function PrinterFormDialog({ mode, initial, winPrinters, winRoutes, winStatus, busy, onClose, onSave }) {
  const [name, setName] = useState(initial?.name || "");
  const [windowsName, setWindowsName] = useState(initial?.windows_printer_name || "");
  const [printerType, setPrinterType] = useState(initial?.printer_type || "kitchen");
  const [isDefault, setIsDefault] = useState(!!initial?.is_default);
  const [isReports, setIsReports] = useState(!!initial?.is_reports);
  const [active, setActive] = useState(initial?.active !== false);
  const hasV3Routes = (winRoutes || []).length > 0;
  // When v3 routes are available, default to the route picker; only fall back
  // to manual entry if the operator explicitly wants a Windows printer name
  // (e.g. legacy v2 simple agent setup).
  const [useManual, setUseManual] = useState(
    !hasV3Routes && (
      winStatus !== "ok" ||
      !!(initial?.windows_printer_name && !(winPrinters || []).includes(initial.windows_printer_name))
    ),
  );
  // Validation: if v3 routes are available AND the value is not in the
  // route list AND not in the Windows printers list, surface a warning —
  // this is the «HPRT HM-E300» footgun that produces "Printer profile not
  // found" 500s every poll cycle.
  const v3Invalid = hasV3Routes
    && !!windowsName.trim()
    && !(winRoutes || []).includes(windowsName.trim())
    && !(winPrinters || []).includes(windowsName.trim());

  const submit = () => {
    if (!name.trim()) {
      toast.error("Το όνομα είναι υποχρεωτικό");
      return;
    }
    onSave({
      name: name.trim(),
      windows_printer_name: windowsName.trim(),
      printer_type: printerType,
      is_default: isDefault,
      is_reports: isReports,
      active,
    });
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "new" ? "Νέος Virtual Εκτυπωτής" : `Επεξεργασία: ${initial?.name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Όνομα Virtual Εκτυπωτή</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="π.χ. Κουζίνα, Bar, Α4 Ταμείο"
              className="h-10"
              data-testid="printer-form-name"
              autoFocus
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Το όνομα που θα εμφανίζεται μέσα στο πρόγραμμα (alias).
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs flex items-center gap-1.5">
                {hasV3Routes ? (
                  <>
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-purple-100 text-purple-700 border border-purple-300">v3</span>
                    Route / Windows Printer
                  </>
                ) : "Windows Printer"}
              </Label>
              {winStatus === "ok" && (
                <button
                  type="button"
                  onClick={() => setUseManual((v) => !v)}
                  className="text-[11px] text-blue-600 hover:underline"
                  data-testid="toggle-manual-input"
                >
                  {useManual
                    ? (hasV3Routes ? "Επιλογή v3 route" : "Επιλογή από λίστα")
                    : (hasV3Routes ? "Windows printer αντί για v3 route" : "Χειροκίνητη εισαγωγή")}
                </button>
              )}
            </div>
            {!useManual && winStatus === "ok" && hasV3Routes ? (
              <select
                value={windowsName}
                onChange={(e) => setWindowsName(e.target.value)}
                className="h-10 w-full px-2 border border-purple-300 rounded-md bg-purple-50/30 text-sm font-mono font-bold text-purple-900"
                data-testid="printer-form-v3-route-select"
              >
                <option value="">— Επιλέξτε route —</option>
                {winRoutes.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            ) : !useManual && winStatus === "ok" ? (
              <select
                value={windowsName}
                onChange={(e) => setWindowsName(e.target.value)}
                className="h-10 w-full px-2 border border-slate-300 rounded-md bg-white text-sm"
                data-testid="printer-form-windows-select"
              >
                <option value="">— Default Windows printer —</option>
                {winPrinters.map((wp) => (
                  <option key={wp} value={wp}>{wp}</option>
                ))}
              </select>
            ) : (
              <Input
                value={windowsName}
                onChange={(e) => setWindowsName(e.target.value)}
                placeholder={hasV3Routes ? "π.χ. kitchen, bar, tameio" : "π.χ. EPSON TM-T20III"}
                className="h-10 font-mono"
                data-testid="printer-form-windows-name"
              />
            )}
            {v3Invalid && (
              <div className="mt-1.5 text-[11px] text-red-700 bg-red-50 border border-red-300 rounded px-2 py-1.5" data-testid="printer-form-v3-warning">
                <b>⚠ Προσοχή:</b> Η τιμή «<code>{windowsName.trim()}</code>» δεν είναι valid v3 route ούτε εγκατεστημένος Windows printer.
                <br />
                Διαθέσιμα v3 routes: <code className="font-bold">{winRoutes.join(", ")}</code>.
                <br />
                Με αυτή την τιμή τα print jobs θα αποτυγχάνουν με «Printer profile not found».
              </div>
            )}
            <div className="text-[11px] text-slate-500 mt-1">
              {hasV3Routes
                ? "Με FText Print Engine v3 χρησιμοποίησε ένα route id (π.χ. kitchen, bar, tameio). Το v3 routing κάνει τη μετατροπή σε φυσικό εκτυπωτή με βάση το dashboard στο http://127.0.0.1:9100/dashboard."
                : winStatus === "ok"
                ? "Διάλεξε από τους εγκατεστημένους εκτυπωτές Windows ή πληκτρολόγησε χειροκίνητα."
                : "Πληκτρολόγησε ακριβώς το όνομα όπως φαίνεται στις Συσκευές & Εκτυπωτές των Windows."}
            </div>
          </div>

          <div>
            <Label className="text-xs">Τύπος Εκτύπωσης</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[
                { id: "kitchen", title: "Κουζίνας (80mm)", sub: "76 χαρ./γραμμή" },
                { id: "a4", title: "Α4", sub: "σελίδα Α4" },
              ].map((opt) => {
                const active = printerType === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPrinterType(opt.id)}
                    data-testid={`printer-form-type-${opt.id}`}
                    className={`text-left rounded-md border-2 p-2.5 transition-all ${
                      active
                        ? "border-blue-600 bg-blue-50"
                        : "border-slate-200 bg-white hover:border-slate-400"
                    }`}
                  >
                    <div className="font-bold text-sm">{opt.title}</div>
                    <div className="text-[11px] text-slate-500">{opt.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch
                checked={isDefault}
                onCheckedChange={setIsDefault}
                data-testid="printer-form-default"
              />
              <span>
                <div className="font-bold text-xs">Default</div>
                <div className="text-[10px] text-slate-500">POS</div>
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch
                checked={isReports}
                onCheckedChange={setIsReports}
                data-testid="printer-form-reports"
              />
              <span>
                <div className="font-bold text-xs">Αναφορές</div>
                <div className="text-[10px] text-slate-500">Βάρδιες/Z</div>
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch
                checked={active}
                onCheckedChange={setActive}
                data-testid="printer-form-active"
              />
              <span>
                <div className="font-bold text-xs">Ενεργός</div>
                <div className="text-[10px] text-slate-500">διαθέσιμος</div>
              </span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <XIcon size={14} className="mr-1" /> Άκυρο
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="printer-form-save"
          >
            <Check size={14} className="mr-1" /> {busy ? "Αποθήκευση..." : "Αποθήκευση"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
/** Companion status panel — probes the local PontePOS Companion (WIN APP)
 * on 127.0.0.1:9100 and shows engine / version / Windows printers / aliases.
 * Typography is NOT configured here any more: it lives in «Πρότυπα
 * Εκτύπωσης» (Διαχείριση) and ships to the Companion as ready ESC/POS
 * bytes via the /raw endpoint — the agent prints them verbatim.
 */
function CompanionStatusPanel() {
  const [health, setHealth] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const probe = async () => {
    setLoading(true);
    setErr(null);
    try {
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 4000);
      const [hRes, pRes] = await Promise.all([
        fetch(`${COMPANION_URL}/health`, { signal: ctrl.signal }),
        fetch(`${COMPANION_URL}/printers`, { signal: ctrl.signal }).catch(() => null),
      ]);
      clearTimeout(tid);
      if (!hRes.ok) throw new Error(`HTTP ${hRes.status}`);
      setHealth(await hRes.json());
      if (pRes && pRes.ok) {
        const p = await pRes.json();
        setPrinters(Array.isArray(p.printers) ? p.printers : []);
      } else {
        setPrinters([]);
      }
    } catch (e) {
      setErr(e.message || "Αδυναμία σύνδεσης");
      setHealth(null);
      setPrinters([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { probe(); }, []);

  const online = !!health?.ok;
  const aliases = health?.printer_profiles && typeof health.printer_profiles === "object"
    ? Object.entries(health.printer_profiles).filter(([, v]) => typeof v === "string")
    : [];

  return (
    <Card className="p-4 space-y-3 mt-4" data-testid="companion-status-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-base font-semibold flex items-center gap-2">
            🖨️ PontePOS Companion (WIN APP)
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${online ? "bg-emerald-500" : "bg-red-400"}`}
              data-testid="companion-status-dot"
            />
            <span className={`text-[11px] font-normal ${online ? "text-emerald-700" : "text-red-600"}`}>
              {loading ? "..." : online ? "Συνδεδεμένο" : "Εκτός σύνδεσης"}
            </span>
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Ο τοπικός agent εκτύπωσης &amp; φορολογικής γέφυρας στο POS PC (port 9100).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={probe} disabled={loading} data-testid="companion-status-refresh">
          {loading ? "..." : "Ανανέωση"}
        </Button>
      </div>

      {err && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid="companion-offline-msg">
          Ο Companion δεν απαντά στο 127.0.0.1:9100 — βεβαιωθείτε ότι το PontePOS-Companion.exe
          τρέχει στο PC του ταμείου (εικονίδιο στο tray).
        </div>
      )}

      {online && (
        <div className="space-y-2 text-xs">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><span className="text-slate-500">Engine:</span> <b className="font-mono">{health.name || "—"}</b></span>
            <span><span className="text-slate-500">Έκδοση:</span> <b className="font-mono">{health.version || "—"}</b></span>
            <span>
              <span className="text-slate-500">Spooler (win32print):</span>{" "}
              {health.win32print ? <b className="text-emerald-700">OK</b> : <b className="text-red-600">Μη διαθέσιμος</b>}
            </span>
            {health.default_printer && (
              <span><span className="text-slate-500">Προεπιλογή Windows:</span> <b className="font-mono">{health.default_printer}</b></span>
            )}
          </div>

          {printers.length > 0 && (
            <div>
              <div className="text-slate-500 mb-1">Εκτυπωτές Windows ({printers.length}):</div>
              <div className="flex flex-wrap gap-1.5">
                {printers.map((p) => (
                  <span key={p} className="px-2 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[11px]" data-testid={`companion-printer-${p}`}>
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}

          {aliases.length > 0 && (
            <div>
              <div className="text-slate-500 mb-1">Aliases (config.json του Companion):</div>
              <table className="text-[11px] border border-slate-200 rounded">
                <tbody>
                  {aliases.map(([alias, target]) => (
                    <tr key={alias} className="border-t first:border-t-0">
                      <td className="px-2 py-0.5 font-mono font-semibold text-purple-700">{alias}</td>
                      <td className="px-2 py-0.5 text-slate-400">→</td>
                      <td className="px-2 py-0.5 font-mono">{target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-1.5" data-testid="companion-typography-note">
        🎨 <b>Τυπογραφία:</b> Η μορφοποίηση (bold, κεντράρισμα, μέγεθος) ρυθμίζεται πλέον από τα{" "}
        <a href="/admin" className="text-blue-600 font-semibold hover:underline">Πρότυπα Εκτύπωσης</a>{" "}
        στη Διαχείριση. Το POS στέλνει έτοιμα ESC/POS bytes στον Companion, ώστε το χαρτί να
        βγαίνει ΑΚΡΙΒΩΣ όπως το preview — χωρίς καμία ρύθμιση στο WIN APP.
      </div>
    </Card>
  );
}





function QuickNotesTab() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const r = await http.get("/quick-notes");
      setNotes(r.data || []);
    } catch (e) {
      toast.error("Αποτυχία φόρτωσης σχολίων");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const add = async () => {
    const text = newText.trim();
    if (!text) { toast.error("Πληκτρολογήστε σχόλιο"); return; }
    try {
      await http.post("/quick-notes", { text, display_order: notes.length });
      setNewText("");
      await reload();
      toast.success("Προστέθηκε");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await http.put(`/quick-notes/${editingId}`, { text: editText.trim() });
      setEditingId(null); setEditText("");
      await reload();
      toast.success("Αποθηκεύτηκε");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    }
  };

  const remove = async (id) => {
    if (!(await confirmDialog("Διαγραφή σχολίου;"))) return;
    try {
      await http.delete(`/quick-notes/${id}`);
      await reload();
      toast.success("Διαγράφηκε");
    } catch (e) {
      toast.error("Αποτυχία διαγραφής");
    }
  };

  const move = async (id, delta) => {
    const idx = notes.findIndex((n) => n.id === id);
    if (idx < 0) return;
    const swap = idx + delta;
    if (swap < 0 || swap >= notes.length) return;
    const a = notes[idx], b = notes[swap];
    try {
      await Promise.all([
        http.put(`/quick-notes/${a.id}`, { display_order: b.display_order }),
        http.put(`/quick-notes/${b.id}`, { display_order: a.display_order }),
      ]);
      await reload();
    } catch (e) { toast.error("Αποτυχία αναδιάταξης"); }
  };

  return (
    <Card className="p-6 space-y-4 max-w-3xl" data-testid="quicknotes-tab">
      <div>
        <h3 className="font-heading text-lg font-bold">Έτοιμα Σχόλια</h3>
        <p className="text-xs text-slate-500 mt-1">
          Φτιάξτε λίστα από συχνά σχόλια που οι χειριστές θα μπορούν να εκτυπώσουν αμέσως
          σε έναν ή περισσότερους εκτυπωτές, από το μενού «Εργασίες → Σχόλια» κάθε τραπεζιού.
          Τα σχόλια δεν αποθηκεύονται στο τραπέζι — εκτυπώνονται μόνο.
        </p>
      </div>
      <div className="flex gap-2">
        <Input
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="π.χ. Κερασμένο από τον Νίκο"
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          data-testid="quicknote-input"
        />
        <Button onClick={add} data-testid="quicknote-add" disabled={!newText.trim()}>
          Προσθήκη
        </Button>
      </div>
      <div className="border rounded-md divide-y">
        {loading && <div className="p-4 text-center text-slate-400">Φόρτωση...</div>}
        {!loading && notes.length === 0 && (
          <div className="p-4 text-center text-slate-400 italic" data-testid="quicknotes-empty">
            Δεν υπάρχουν έτοιμα σχόλια ακόμα.
          </div>
        )}
        {notes.map((n, i) => (
          <div key={n.id} className="p-2 flex items-center gap-2" data-testid={`quicknote-row-${n.id}`}>
            <div className="flex flex-col">
              <button
                onClick={() => move(n.id, -1)}
                disabled={i === 0}
                className="text-xs text-slate-500 hover:text-slate-800 disabled:opacity-30"
                aria-label="Πάνω"
              >▲</button>
              <button
                onClick={() => move(n.id, 1)}
                disabled={i === notes.length - 1}
                className="text-xs text-slate-500 hover:text-slate-800 disabled:opacity-30"
                aria-label="Κάτω"
              >▼</button>
            </div>
            {editingId === n.id ? (
              <>
                <Input
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") { setEditingId(null); setEditText(""); } }}
                  autoFocus
                  data-testid={`quicknote-edit-input-${n.id}`}
                />
                <Button size="sm" onClick={saveEdit} data-testid={`quicknote-edit-save-${n.id}`}>Αποθήκευση</Button>
                <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setEditText(""); }}>Άκυρο</Button>
              </>
            ) : (
              <>
                <div className="flex-1 text-sm" data-testid={`quicknote-text-${n.id}`}>{n.text}</div>
                <Button size="sm" variant="outline" onClick={() => { setEditingId(n.id); setEditText(n.text); }} data-testid={`quicknote-edit-${n.id}`}>Επεξεργασία</Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => remove(n.id)} data-testid={`quicknote-delete-${n.id}`}>Διαγραφή</Button>
              </>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}


function CompanionAutoPrintToggle() {
  const [enabled, setEnabled] = useState(isCompanionEnabled());
  const [status, setStatus] = useState({ companionOk: null, pendingCount: 0, lastError: null, lastSuccessAt: null });
  const [winPrinters, setWinPrinters] = useState(null); // {printers:[], default:""} | {error:""}
  const [probing, setProbing] = useState(false);
  useEffect(() => subscribeCompanion(setStatus), []);
  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    setCompanionEnabled(next);
    if (next) toast.success("Αυτόματη εκτύπωση: ενεργοποιήθηκε");
    else toast.success("Αυτόματη εκτύπωση: απενεργοποιήθηκε");
  };
  const probe = async () => {
    setProbing(true);
    setWinPrinters(null);
    try {
      const r = await discoverWindowsPrinters();
      setWinPrinters(r);
      if (r.error) toast.error(`Companion: ${r.error}`);
    } finally { setProbing(false); }
  };
  const copyName = async (name) => {
    try { await navigator.clipboard.writeText(name); toast.success(`Αντιγράφηκε: ${name}`); }
    catch { toast.error("Αποτυχία αντιγραφής"); }
  };
  return (
    <div
      className={`rounded-md border-2 p-3 text-sm ${
        enabled ? "bg-blue-50 border-blue-300" : "bg-slate-50 border-slate-200"
      }`}
      data-testid="companion-autoprint-toggle"
    >
      <div className="flex items-start gap-3">
        <button
          onClick={toggle}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 transition-colors ${
            enabled ? "bg-blue-600 border-blue-700" : "bg-slate-300 border-slate-400"
          }`}
          role="switch"
          aria-checked={enabled}
          data-testid="companion-autoprint-switch"
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform mt-0.5 ${
              enabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-800">Αυτόματη Εκτύπωση μέσω Companion (αυτή η συσκευή)</div>
          <div className="text-xs text-slate-600 mt-0.5">
            Όταν είναι ενεργό, αυτή η συσκευή τραβάει pending print jobs από τον server κάθε 3 sec
            και τα στέλνει στους τοπικούς εκτυπωτές μέσω <code>localhost:9100</code>.
            Μόνο 1 συσκευή ανά κατάστημα πρέπει να το έχει ενεργό (το POS PC).
          </div>
          {enabled && (
            <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
              <div className="bg-white rounded px-2 py-1 border">
                <div className="text-slate-500">Pending</div>
                <div className="font-bold text-base text-slate-800" data-testid="companion-pending-count">{status.pendingCount ?? 0}</div>
              </div>
              <div className="bg-white rounded px-2 py-1 border">
                <div className="text-slate-500">Companion</div>
                <div className={`font-bold text-base ${status.companionOk === true ? "text-emerald-700" : status.companionOk === false ? "text-red-600" : "text-slate-500"}`}>
                  {status.companionOk === true ? "ONLINE" : status.companionOk === false ? "ERROR" : "—"}
                </div>
              </div>
              <div className="bg-white rounded px-2 py-1 border">
                <div className="text-slate-500">Last OK</div>
                <div className="font-mono text-[11px] truncate text-slate-700">
                  {status.lastSuccessAt ? new Date(status.lastSuccessAt).toLocaleTimeString("el-GR") : "—"}
                </div>
              </div>
              {status.lastError && (
                <div className="col-span-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 break-all">
                  ⚠ {status.lastError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Discover Windows printers — always visible, helps the operator
          fix the «HTTP 500: printer not found» case where the
          windows_printer_name in the profile doesn't match what Windows
          actually has installed. Also useful for initial setup. */}
      <div className="mt-3 pt-3 border-t border-blue-200">
        <Button
          size="sm"
          variant="outline"
          onClick={probe}
          disabled={probing}
          className="text-xs"
          data-testid="companion-discover-printers"
        >
          <RefreshCw size={12} className={`mr-1 ${probing ? "animate-spin" : ""}`} />
          {probing ? "Ανίχνευση..." : "Δες τα Windows Printers"}
        </Button>
        {winPrinters && (
          <div className="mt-2">
            {winPrinters.error ? (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-2 break-all">
                <b>Σφάλμα Companion:</b> {winPrinters.error}
                <div className="mt-1 text-[10px] text-red-600">
                  Βεβαιωθείτε ότι το <code>FText_Print_Agent_v2.py</code> τρέχει στο τοπικό PC (port 9100).
                </div>
              </div>
            ) : (
              <div className="bg-white border border-blue-200 rounded p-2 space-y-2">
                {winPrinters.version && (
                  <div className="text-[10px] text-emerald-700 font-mono bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                    Companion v{winPrinters.version} detected
                  </div>
                )}
                {Array.isArray(winPrinters.routes) && winPrinters.routes.length > 0 && (
                  <div>
                    <div className="text-[11px] text-purple-700 font-bold mb-1">
                      🎯 Routes/Profiles (v3 print engine) — αυτά χρησιμοποιεί ο δικός σας Companion:
                    </div>
                    <div className="space-y-0.5">
                      {winPrinters.routes.map((r) => (
                        <div key={`route-${r}`} className="flex items-center gap-2 text-xs py-0.5">
                          <button
                            onClick={() => copyName(r)}
                            className="text-purple-600 hover:underline text-[10px]"
                            data-testid={`win-route-copy-${r}`}
                          >📋 Αντιγραφή</button>
                          <code className="flex-1 truncate text-purple-700 font-bold">{r}</code>
                        </div>
                      ))}
                    </div>
                    <div className="mt-1 text-[10px] text-purple-600 italic">
                      Για v3 print engine: βάλτε ένα από αυτά τα route IDs στο πεδίο «Windows printer name» κάθε profile (π.χ. `kitchen`, `bar`).
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-[11px] text-slate-500 font-bold mb-1">
                    Εγκατεστημένοι εκτυπωτές στα Windows (default: <span className="text-emerald-700">{winPrinters.default}</span>)
                  </div>
                  <div className="space-y-0.5">
                    {(winPrinters.printers || []).map((p) => (
                      <div key={p} className="flex items-center gap-2 text-xs py-0.5">
                        <button
                          onClick={() => copyName(p)}
                          className="text-blue-600 hover:underline text-[10px]"
                          data-testid={`win-printer-copy-${p}`}
                        >📋 Αντιγραφή</button>
                        <code className={`flex-1 truncate ${p === winPrinters.default ? "font-bold text-emerald-700" : "text-slate-700"}`}>
                          {p}
                        </code>
                      </div>
                    ))}
                  </div>
                  <div className="mt-1 text-[10px] text-slate-500 italic">
                    Για v2 print agent: βάλτε ένα από αυτά τα Windows printer names στο πεδίο «Windows printer name» κάθε profile.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function PrintJobsMonitor({ printers }) {
  const [jobs, setJobs] = useState([]);
  const [filterPrinter, setFilterPrinter] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pending: "false", limit: "100" });
      if (filterPrinter) params.set("printer_id", filterPrinter);
      const r = await http.get(`/print-jobs?${params.toString()}`);
      setJobs(r.data || []);
    } catch {
      /* silent */
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!expanded) return;
    reload();
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [expanded, filterPrinter]);

  const visibleJobs = useMemo(() => {
    if (!filterStatus) return jobs;
    if (filterStatus === "failed") return jobs.filter((j) => (j.retry_count || 0) > 0);
    return jobs.filter((j) => j.status === filterStatus);
  }, [jobs, filterStatus]);

  const stats = useMemo(() => {
    const s = { pending: 0, printed: 0, failedish: 0 };
    for (const j of jobs) {
      if (j.status === "printed") s.printed += 1;
      else if (j.status === "pending") s.pending += 1;
      if ((j.retry_count || 0) > 0) s.failedish += 1;
    }
    return s;
  }, [jobs]);

  const retry = async (id) => {
    try {
      await http.post(`/print-jobs/${id}/retry`);
      toast.success("Επαναπρογραμματίστηκε");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    }
  };

  const cleanupOld = async () => {
    if (!(await confirmDialog("Διαγραφή τυπωμένων jobs άνω των 24 ωρών;"))) return;
    try {
      const r = await http.delete("/print-jobs/cleanup");
      toast.success(`Διαγράφηκαν ${r.data?.deleted || 0} jobs`);
      reload();
    } catch { toast.error("Αποτυχία cleanup"); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  return (
    <div className="rounded-md border-2 border-slate-200 bg-white" data-testid="print-jobs-monitor">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50"
        data-testid="print-monitor-toggle"
      >
        <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
          📊 Παρακολούθηση Εκτυπώσεων
          <span className="text-[11px] font-normal text-slate-500">
            ({stats.pending} pending · {stats.printed} printed · {stats.failedish} με retries)
          </span>
        </div>
        <span className="text-slate-500">{expanded ? "▼" : "▶"}</span>
      </button>

      {expanded && (
        <div className="border-t-2 border-slate-200 p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={filterPrinter}
              onChange={(e) => setFilterPrinter(e.target.value)}
              className="border-2 border-slate-200 rounded px-2 py-1 text-xs"
              data-testid="print-monitor-filter-printer"
            >
              <option value="">Όλοι οι εκτυπωτές</option>
              {(printers || []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border-2 border-slate-200 rounded px-2 py-1 text-xs"
              data-testid="print-monitor-filter-status"
            >
              <option value="">Όλα τα status</option>
              <option value="pending">Pending</option>
              <option value="printed">Printed</option>
              <option value="failed">Με retries</option>
            </select>
            <Button size="sm" variant="outline" onClick={reload} disabled={loading} className="text-xs h-8">
              <RefreshCw size={12} className="mr-1" /> Refresh
            </Button>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={cleanupOld} className="text-xs h-8 text-red-600 border-red-300">
              <Trash2 size={12} className="mr-1" /> Cleanup &gt; 24h
            </Button>
          </div>

          {visibleJobs.length === 0 ? (
            <div className="text-center text-xs text-slate-400 italic py-6 border-2 border-dashed border-slate-200 rounded">
              Καμία εκτύπωση στο ιστορικό.
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto border border-slate-200 rounded">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left font-bold text-slate-700">Ώρα</th>
                    <th className="px-2 py-1 text-left font-bold text-slate-700">Εκτυπωτής</th>
                    <th className="px-2 py-1 text-left font-bold text-slate-700">Τύπος</th>
                    <th className="px-2 py-1 text-left font-bold text-slate-700">Τραπέζι</th>
                    <th className="px-2 py-1 text-center font-bold text-slate-700">Status</th>
                    <th className="px-2 py-1 text-center font-bold text-slate-700">Retries</th>
                    <th className="px-2 py-1 text-right font-bold text-slate-700">Ενέργεια</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleJobs.map((j) => {
                    const isFailed = (j.retry_count || 0) > 0 && j.status === "pending";
                    const rowBg = j.status === "printed"
                      ? "bg-emerald-50"
                      : isFailed
                        ? "bg-red-50"
                        : "bg-amber-50";
                    return (
                      <tr key={j.id} className={`${rowBg} border-b border-slate-100`} data-testid={`print-job-row-${j.id}`}>
                        <td className="px-2 py-1 font-mono">{fmtTime(j.printed_at || j.created_at)}</td>
                        <td className="px-2 py-1 truncate max-w-[140px]" title={j.windows_printer_name}>{j.windows_printer_name}</td>
                        <td className="px-2 py-1 uppercase text-slate-600">{j.kind}</td>
                        <td className="px-2 py-1 truncate max-w-[100px]">{j.table_name || "—"}</td>
                        <td className="px-2 py-1 text-center">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            j.status === "printed" ? "bg-emerald-600 text-white"
                              : isFailed ? "bg-red-600 text-white"
                                : "bg-amber-500 text-white"
                          }`}>
                            {j.status}
                          </span>
                        </td>
                        <td className="px-2 py-1 text-center font-mono">
                          {(j.retry_count || 0) > 0 ? <span className="text-red-700 font-bold">{j.retry_count}</span> : "—"}
                        </td>
                        <td className="px-2 py-1 text-right">
                          {j.status !== "printed" && (
                            <button
                              onClick={() => retry(j.id)}
                              className="text-blue-600 hover:underline text-[11px]"
                              data-testid={`print-job-retry-${j.id}`}
                            >Επανάληψη</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* Surface failed jobs' error messages — collapsed under a details so it doesn't clutter the main table */}
          {visibleJobs.some((j) => j.last_error) && (
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-900 font-bold">
                ⚠ Σφάλματα ({visibleJobs.filter((j) => j.last_error).length})
              </summary>
              <div className="mt-1 space-y-1">
                {visibleJobs.filter((j) => j.last_error).slice(0, 10).map((j) => (
                  <div key={`err-${j.id}`} className="bg-red-50 border border-red-200 rounded px-2 py-1 text-red-900">
                    <span className="font-mono text-[10px] text-red-700">{j.windows_printer_name}:</span>{" "}
                    {j.last_error}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


function KioskModeToggle() {
  const [enabled, setEnabled] = useState(isKioskMode());
  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    setKioskMode(next);
    toast.success(next ? "Kiosk Mode: ενεργοποιήθηκε" : "Kiosk Mode: απενεργοποιήθηκε");
  };
  const testPrint = () => {
    // Print a tiny test slip via the same path the real prints use —
    // if Chrome is launched with --kiosk-printing this fires silently.
    const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Kiosk Test</title>
<style>body{font-family:monospace;font-size:14px;padding:10px}
h1{text-align:center;font-size:18px;margin:5px 0}
.row{display:flex;justify-content:space-between;border-top:1px dashed #000;padding:4px 0}
</style></head><body>
<h1>★ KIOSK TEST ★</h1>
<div class="row"><span>Ημερομηνία</span><b>${new Date().toLocaleString("el-GR")}</b></div>
<div class="row"><span>Mode</span><b>${enabled ? "ENABLED" : "DISABLED"}</b></div>
<div class="row"><span>Status</span><b>Αυτό το χαρτάκι βγήκε χωρίς διάλογο</b></div>
<p style="text-align:center;margin-top:20px">Αν δεν εκτυπώθηκε, ελέγξτε τα Chrome flags.</p>
</body></html>`;
    // Use the same iframe path real prints use when kiosk is on.
    // (We bypass the localStorage check because we want to test even
    //  while disabled — the print dialog will show if not in kiosk mode.)
    let frame = document.getElementById("kiosk-print-frame");
    if (frame) frame.remove();
    frame = document.createElement("iframe");
    frame.id = "kiosk-print-frame";
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
    document.body.appendChild(frame);
    frame.onload = () => {
      setTimeout(() => {
        try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch { /* noop */ }
      }, 200);
    };
    const doc = frame.contentDocument || frame.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
  };
  return (
    <div
      className={`rounded-md border-2 p-3 text-sm ${
        enabled ? "bg-purple-50 border-purple-300" : "bg-slate-50 border-slate-200"
      }`}
      data-testid="kiosk-mode-toggle"
    >
      <div className="flex items-start gap-3">
        <button
          onClick={toggle}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 transition-colors ${
            enabled ? "bg-purple-600 border-purple-700" : "bg-slate-300 border-slate-400"
          }`}
          role="switch"
          aria-checked={enabled}
          data-testid="kiosk-mode-switch"
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform mt-0.5 ${
              enabled ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-800">Kiosk Mode — Silent HTML Print (αυτή η συσκευή)</div>
          <div className="text-xs text-slate-600 mt-0.5 space-y-1.5">
            <p>
              Καλύπτει τα HTML-based prints (Προφόρμα, Kitchen ticket popup) που το Companion δεν χειρίζεται.
              Όταν είναι ON, τυπώνουμε μέσω hidden iframe αντί για popup window — χωρίς να ξεφεύγει το focus.
            </p>
            <p className="bg-amber-50 border border-amber-200 rounded px-2 py-1">
              <b>Απαιτεί</b>: ο Chrome να έχει ξεκινήσει με flag <code className="bg-amber-100 px-1 rounded text-[11px]">--kiosk-printing</code>.
              Φτιάξτε shortcut: <code className="bg-amber-100 px-1 rounded text-[10px] block mt-1 whitespace-pre-wrap break-all">{`"C:\\Program Files\\Google\\Chrome\\Application\\chrome.exe" --kiosk-printing --app=https://register-manager-3.preview.emergentagent.com`}</code>
            </p>
            <p>
              Επίσης ορίστε τον σωστό printer ως <b>default</b> στα Windows ώστε το silent print να
              πάει εκεί. Συνδυάστε με το Companion Mode (από πάνω) για multi-printer routing.
            </p>
          </div>
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={testPrint}
              className="text-xs"
              data-testid="kiosk-mode-test"
            >
              <Printer size={12} className="mr-1" /> Δοκιμή Εκτύπωσης
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

